import {
  Fragment,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
} from "react";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";

import { arrayMove, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MatrixColumnScale } from "@/components/MatrixGrid/MatrixColumnScale";
import { MatrixGrid } from "@/components/MatrixGrid/MatrixGrid";
import { MatrixHeaderField } from "@/components/MatrixGrid/MatrixHeaderField";
import {
  MatrixGridColumn,
  MatrixGridRow,
} from "@/components/MatrixGrid/types";
import { SortableItem, SortableList } from "@/components/SortableList/SortableList";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { LiftHalfChip } from "./LiftHalfChip";
import { QuestionLogicSection } from "./QuestionLogicSection";
import { QuestionSettingsMenu } from "./QuestionSettingsMenu";
import { TopicCoverageChip, type CoverageEntry } from "./TopicCoverageChip";
import { ValidationIssueChip } from "./ValidationIssueChip";
import {
  type AssetMentionTables,
  withEditableAssetRows,
} from "@/features/studies/questionnaire/assets/conceptAssets";
import { readQuestionLiftHalf } from "@/features/studies/questionnaire/assets/liftTags";
import type {
  ChoiceOption,
  ChoicesFromRef,
  WinnerCompositeDefinition,
  ConceptAsset,
  ContinuousResponse,
  ContinuousUnitId,
  DurationUnit,
  MatrixRow,
  QuestionRoutingRule,
  QuestionVisibilityRule,
  QuestionVisibilityRuleExpr,
  QuestionnaireExposureZone,
  QuestionnaireValidationIssue,
  ResponseDefinition,
  UnitRegistryEntry,
  VisibilityRuleJoin,
} from "@digital-twins/shared-types";
import {
  QuestionExposureSettings,
  QuestionLogicKind,
  QuestionSettingsLogicItem,
  RoutingOptionOutcome,
} from "./questionSettingsTypes";
import {
  answersWithComparisonParticipants,
  conditionMembershipOperator,
  conditionOperatorFamily,
  conditionOperatorReads,
  conditionOperatorValueCount,
  CONTINUOUS_UNIT_REGISTRY,
  DURATION_EXPOSED_UNITS,
  getEffectiveCurrencyCode,
  getUnitRegistryEntry,
  getVisibilityRuleLeaves,
  isAssetBackedChoice,
  isRatingUnitId,
  isVisibilityRuleGroup,
  MAX_MATRIX_CATEGORIES,
  MAX_MATRIX_MEASURES,
  MAX_VISIBILITY_RULE_GROUP_SIZE,
  continuousRangeHint,
  defaultEndSurveyDisposition,
  durationUnitLabel,
  optionPlaceholderLabel,
  ownPriceVersion,
  questionRefLabel,
  ratingBounds as registryRatingBounds,
  ratingHasMidpoint,
  ratingMidpointLabel,
  removingOptionLeavesTooFewParticipants,
  authorAnswerAssetLabel,
  respondentScaleOptions,
  respondentScalePoints,
  respondentWordingFor,
  carriedChoiceOptions,
  gridRowsRandomized,
} from "@digital-twins/shared-types";
import {
  Check,
  ChevronDown,
  Copy,
  EyeOff,
  Filter,
  GitBranch,
  GripVertical,
  Plus,
  Trash2,
  X,
} from "lucide-react";

import {
  CanonicalEditableQuestion,
  ChoicesFromCandidate,
  canonicalEditors,
  defaultSkipTargetStableKey,
  extractChoiceOptions,
  extractColumns,
  extractRows,
  getTypeOption,
  getTypeOptions,
  isImmediateNextSkipTarget,
  midpointIndex,
  QuestionnaireQuestionRecord,
  reachableChoicesFromModes,
  RoutingTargetCandidate,
  VisibilityRuleCandidate,
} from "../hooks/useQuestionnaireWorkspace";
import {
  ratingScaleTemplatesFor,
  type RatingScaleTemplateKey,
} from "../ratingScaleTemplates";
import { questionNumberedTitle } from "../utils/displayLabel";
import { formatIssueCopy, ruleCopy } from "../utils/questionnaireIssueCopy";
import {
  routingOutcomeByOptionValue,
  summarizeChoicesFrom,
  summarizeRoutingRule,
  summarizeVisibilityRule,
} from "../utils/logicSummary";
import { ConceptMentionField } from "./ConceptMentionField";
import { revealAssetCard } from "./ConceptAssetsPanel";

const SCALE_MIN_LABELS: Record<string, string> = {
  en: "Low", es: "Bajo", fr: "Faible", de: "Niedrig", pt: "Baixo",
  it: "Basso", nl: "Laag", tr: "Düşük", ru: "Низкий", ar: "منخفض",
  he: "נמוך", hi: "कम", ja: "低い", zh: "低", ko: "낮음",
};
const SCALE_MAX_LABELS: Record<string, string> = {
  en: "High", es: "Alto", fr: "Élevé", de: "Hoch", pt: "Alto",
  it: "Alto", nl: "Hoog", tr: "Yüksek", ru: "Высокий", ar: "مرتفع",
  he: "גבוה", hi: "उच्च", ja: "高い", zh: "高", ko: "높음",
};

interface QuestionnaireQuestionCardProps {
  conceptAssets: Array<ConceptAsset>;
  /** The saved and pending catalogs' mention tables (`AssetMentionTables`). */
  mentionTables: AssetMentionTables;
  /** Advisory topic-coverage result for this question, or `'computing'`. */
  coverageResult?: CoverageEntry;
  /**
   * Where this question sits against the panel's two declared exposure marks
   * (Phase 4b-d), computed once by `QuestionnaireBuilderPanel` from
   * `draft.exposureBoundary` so this card never re-detects it. Stamped as
   * `data-exposure-zone` for the builder specs to read.
   */
  exposureZone?: QuestionnaireExposureZone;
  /**
   * Asked with the assets off screen while its zone shows them (B6 F1, owner
   * decision 2026-10-14), resolved once per render by
   * `QuestionnaireBuilderPanel` from the runner's delivery (`askedOffScreen`).
   * The one per-question exposure fact the card states.
   */
  askedOffScreen?: boolean;
  /** Blocking validation issues indexed onto this question. */
  validationIssues?: Array<QuestionnaireValidationIssue>;
  /**
   * The saved verdict's advice on this question (QA4 N3: a price threshold
   * after a priced stimulus was computed and shown nowhere). Guidance in the
   * card's notice, never the "Needs fix" chip.
   */
  advisoryIssues?: Array<QuestionnaireValidationIssue>;
  /**
   * Composites this question feeds (structural metricRef ∩ component id). Drives
   * the "feeds {score}" orientation chip. Empty for non-scored questions and for
   * non-LP studies (so no chip ever appears there).
   */
  feedsComposites?: Array<WinnerCompositeDefinition>;
  /** Earlier questions this question's skip logic may depend on. */
  dependencyCandidates?: Array<VisibilityRuleCandidate>;
  /**
   * Earlier + host candidates for routingRule.when;
   * additional compound leaves may pick any of these.
   */
  routingWhenCandidates?: Array<VisibilityRuleCandidate>;
  /** Later questions a skip_to routingRule may target. */
  routingTargetCandidates?: Array<RoutingTargetCandidate>;
  /** Earlier select questions this question may carry forward choices from. */
  choicesFromCandidates?: Array<ChoicesFromCandidate>;
  index: number;
  isSelected: boolean;
  language?: string;
  /**
   * The draft's `contentLanguage`: the language the compile writes respondent
   * text in (scale options, N/A). `language` is the builder chip's.
   */
  contentLanguage?: string;
  onChangeType: (nextType: string) => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onSelect: () => void;
  onUpdate: (
    updater: (question: CanonicalEditableQuestion) => CanonicalEditableQuestion,
  ) => void;
  question: QuestionnaireQuestionRecord;
  /**
   * True while a chat turn is in flight. The landing draft replaces this one,
   * so an edit made now would be reverted with no explanation.
   */
  disabled?: boolean;
  /** Copy for `title` on the disabled controls. */
  disabledReason?: string;
  /** Display label per stableKey ("Q2"), for the one-line logic summaries. */
  questionLabelByStableKey?: Map<string, string>;
  /** Labels of the questions reusing this one's options ("Q5", "Q7"). */
  feedsQuestions?: Array<string>;
  /** Exposure line moves for the Settings menu; null when not applicable. */
  exposureSettings?: QuestionExposureSettings | null;
}

export function compositeScoreLabel(
  composite: WinnerCompositeDefinition,
): string {
  return composite.label || composite.output?.displayLabel || composite.id;
}

function choiceOptionsOrDefaults(
  question: CanonicalEditableQuestion,
): Array<ChoiceOption> {
  const options = extractChoiceOptions(question);
  if (options.length > 0) {
    return options;
  }
  // Blank, shown with the "Option N" placeholder, never seeded words (FE11).
  return [1, 2].map((position) => ({
    id: `${question.stableKey || "choice"}_${position}`,
    value: `${question.stableKey || "choice"}_${position}`,
    label: "",
  }));
}

// The option editor's rows, edits, removals and additions all address the one
// list `choiceOptionsOrDefaults` returns (blank labels included), so a row
// index names the same option everywhere (F16).
function withChoiceLabelAt(
  question: CanonicalEditableQuestion,
  index: number,
  label: string,
): CanonicalEditableQuestion {
  return canonicalEditors.responseWithChoiceOptions(
    question,
    choiceOptionsOrDefaults(question).map((option, optionIndex) =>
      optionIndex === index ? { ...option, label } : option,
    ),
  );
}

function withoutChoiceAt(
  question: CanonicalEditableQuestion,
  index: number,
): CanonicalEditableQuestion {
  return canonicalEditors.responseWithChoiceOptions(
    question,
    choiceOptionsOrDefaults(question).filter(
      (_, optionIndex) => optionIndex !== index,
    ),
  );
}

/**
 * Where a list's pinned tail starts: the trailing run of escapes (exclusive
 * or keep-last). An added option goes above it and a dragged one never
 * crosses it, so both share this one boundary. Only the trailing run pins: an
 * escape an older draft left mid-list moves like any other option.
 */
export function anchoredTailStart(options: ReadonlyArray<ChoiceOption>): number {
  let start = options.length;
  while (
    start > 0 &&
    (options[start - 1].exclusive === true ||
      options[start - 1].keepLast === true)
  )
    start -= 1;
  return start;
}

// A comparison's options are its tested assets, each with a typed choice ref
// the server derives; a hand-added option has none, so there it is the
// exclusive opt-out (`asset_comparison_choice_ref_required`), once
// (`canAddChoice`). Assets join or leave in the asset panel. A new option
// starts blank with its placeholder, never seeded words in one language, and
// an ordinary one goes above the pinned tail (`anchoredTailStart`) so it never
// lands between the escapes (QA4 FE11). Its id is the code's (`keepOrMint`).
// The card focuses the new row at the index `addedChoiceIndex` names (RND-2832).
function addedChoiceIndex(question: CanonicalEditableQuestion): number {
  const options = choiceOptionsOrDefaults(question);
  return answersWithComparisonParticipants(question)
    ? options.length
    : anchoredTailStart(options);
}

function withAddedChoice(
  question: CanonicalEditableQuestion,
): CanonicalEditableQuestion {
  const options = choiceOptionsOrDefaults(question);
  const at = addedChoiceIndex(question);
  const added = answersWithComparisonParticipants(question)
    ? { label: "", exclusive: true }
    : { label: "" };
  return canonicalEditors.responseWithChoiceOptions(question, [
    ...options.slice(0, at),
    added,
    ...options.slice(at),
  ]);
}

/**
 * How many leading options the author orders by drag: those above the pinned
 * tail of a list the question owns. A carried list (`choicesFrom`) follows its
 * source, a typed roster (`optionSource`) its tested assets or the study's
 * brand roster, and a comparison's participants the tested assets, so none
 * of them drags.
 */
function authorOrderedOptionCount(question: CanonicalEditableQuestion): number {
  const response = question.response as {
    choicesFrom?: unknown;
    optionSource?: unknown;
  };
  if (
    response.choicesFrom ||
    response.optionSource ||
    answersWithComparisonParticipants(question)
  )
    return 0;
  return anchoredTailStart(choiceOptionsOrDefaults(question));
}

/**
 * A dragged option, written as the reordered list through the one option
 * writer: each option keeps its id and value, and the Save sends the list in
 * its new order like any other option edit.
 */
function withChoiceMoved(
  question: CanonicalEditableQuestion,
  from: number,
  to: number,
): CanonicalEditableQuestion {
  const count = authorOrderedOptionCount(question);
  if (from >= count || to >= count) return question;
  return canonicalEditors.responseWithChoiceOptions(
    question,
    arrayMove(choiceOptionsOrDefaults(question), from, to),
  );
}

/** A comparison takes one opt-out and nothing else by hand. */
function canAddChoice(question: CanonicalEditableQuestion): boolean {
  return !(
    answersWithComparisonParticipants(question) &&
    choiceOptionsOrDefaults(question).some((option) => option.exclusive === true)
  );
}

// Matrix rows and columns follow the option editor's rule: an edit, removal or
// addition addresses the item objects themselves, so a rename keeps the item's
// id and value and a removal removes that item, never one matched by label.
// Rows generated from the tested assets become the author's fixed rows (tags
// intact) on the first edit.
type MatrixRowEdit = Omit<MatrixRow, "id" | "value"> &
  Partial<Pick<MatrixRow, "id" | "value">>;

function fixedMatrixRows(
  question: CanonicalEditableQuestion,
  conceptAssets: Array<ConceptAsset>,
): { question: CanonicalEditableQuestion; rows: Array<MatrixRow> } {
  const editable = withEditableAssetRows(question, conceptAssets);
  const rows = (
    editable.response as {
      rows?: { source?: string; rows?: Array<MatrixRow> };
    }
  ).rows;
  return {
    question: editable,
    rows: rows?.source === "fixed" && Array.isArray(rows.rows) ? rows.rows : [],
  };
}

/**
 * The one tested asset a row or an answer stands for, when its `assetRefs`
 * name exactly one: its mention chip then names that asset's version (F26
 * #16, price versions of one product share its name). A question's title and
 * description name a version only when the question is about one
 * (`ownPriceVersion`, D9), as Preview does; a single-priced product's title
 * keeps its name alone.
 */
function soleAssetId(
  assetRefs: ReadonlyArray<{ assetId: string }> | undefined,
): string | undefined {
  return assetRefs?.length === 1 ? assetRefs[0].assetId : undefined;
}

/** Per displayed grid row, the tested asset it stands for (`soleAssetId`). */
function matrixRowAssetIds(
  question: CanonicalEditableQuestion,
  conceptAssets: Array<ConceptAsset>,
): Array<string | undefined> {
  return fixedMatrixRows(question, conceptAssets).rows.map((row) =>
    soleAssetId(row.assetRefs),
  );
}

function withMatrixRows(
  question: CanonicalEditableQuestion,
  conceptAssets: Array<ConceptAsset>,
  edit: (rows: Array<MatrixRow>) => Array<MatrixRowEdit>,
): CanonicalEditableQuestion {
  const current = fixedMatrixRows(question, conceptAssets);
  return canonicalEditors.responseWithRows(current.question, edit(current.rows));
}

/**
 * A grid's row drag, written through `responseWithRows` like every row edit.
 * Fixed rows are the author's to order; rows drawn from the tested assets
 * follow the asset catalog, so they have none.
 */
function rowMove(
  response: { rows?: { source?: string } },
  updateRows: (edit: (rows: Array<MatrixRow>) => Array<MatrixRowEdit>) => void,
): ((from: number, to: number) => void) | undefined {
  if (response.rows?.source !== "fixed") return undefined;
  return (from, to) => updateRows((rows) => arrayMove(rows, from, to));
}

function matrixColumnItems(
  question: CanonicalEditableQuestion,
): Array<{ id: string; label: string }> {
  const response = question.response as {
    kind?: string;
    measures?: Array<{ id: string; label: string }>;
    categories?: Array<{ id: string; label: string }>;
  };
  if (response.kind === "matrix_rating") return response.measures ?? [];
  if (response.kind === "matrix_categorical") return response.categories ?? [];
  return [];
}

function withMatrixColumns(
  question: CanonicalEditableQuestion,
  edit: (
    columns: Array<{ id: string; label: string }>,
  ) => Array<{ id?: string; label: string }>,
  language: string | undefined,
): CanonicalEditableQuestion {
  return canonicalEditors.responseWithColumns(
    question,
    edit(matrixColumnItems(question)),
    language || "en",
  );
}

function replaceLabelAt<T extends { label: string }>(
  items: Array<T>,
  index: number,
  label: string,
): Array<T> {
  return items.map((item, itemIndex) =>
    itemIndex === index ? { ...item, label } : item,
  );
}

function withoutItemAt<T>(items: Array<T>, index: number): Array<T> {
  return items.filter((_, itemIndex) => itemIndex !== index);
}

const MATRIX_HEADER_MAX_LENGTH = 80;

// The add-row / add-column affordances moved into the shared `MatrixGrid`
// shell (apps/frontend/src/components/MatrixGrid/MatrixGrid.tsx); this card
// now passes their labels and testids in as props.

// The bounds are the shared registry's. A card read before its unit is set
// shows the scale a new measure starts on (`rating_1_5`).
function ratingBounds(unit: string | undefined): [number, number] {
  return registryRatingBounds(
    unit && isRatingUnitId(unit) ? unit : "rating_1_5",
  );
}

/**
 * The stored unit's points, from the registry bounds: what the scale editor
 * edits. A legacy 1-7 keeps its seven here so its labels stay editable; what
 * respondents pick from is `respondentScaleOptions`.
 */
function storedRatingPoints(unit: string | undefined): Array<number> {
  const [min, max] = ratingBounds(unit);
  return Array.from({ length: max - min + 1 }, (_, index) => min + index);
}

function typeLabel(question: CanonicalEditableQuestion) {
  const response = question.response as any;
  if (!("type" in question)) {
    return response.kind === "multi_select" ? "Multi-segment" : "Segment";
  }
  if (question.type === "single_select") return "Single choice";
  if (question.type === "multi_select") return "Multi-select";
  if (question.type === "ranking") return "Ranking";
  if (question.type === "matrix_rating") return "Matrix";
  if (question.type === "matrix_categorical") return "Matrix";
  if (question.type === "open_end") return "Open end";
  if (question.type === "continuous") return "Number";
  if (question.type === "rating") {
    const [min, max] = ratingBounds(response.unit);
    return `Rating · ${min}-${max}`;
  }
  return question.type;
}

function chipTone(question: CanonicalEditableQuestion) {
  const response = question.response as any;
  if (response.kind === "rating") return "coral";
  if (response.kind === "open_end" || response.kind === "continuous")
    return "gray";
  return "green";
}

/** One wording-template entry; the matrix column's picker draws the same row. */
const RATING_TEMPLATE_ITEM_CLASS =
  "flex w-full items-center justify-between gap-2 rounded-[6px] px-2.5 py-1.5 text-left text-[12px] text-foreground transition-colors hover:bg-muted";

const PILL_BASE_CLASS =
  "inline-flex items-center gap-1 rounded-full border px-[10px] py-[3px] text-[11px] font-medium leading-[1.4] transition-colors";

function TypePicker(props: {
  question: CanonicalEditableQuestion;
  onChange: (nextType: string) => void;
  questionId: string;
  disabled?: boolean;
  disabledReason?: string;
}) {
  const { onChange, question, questionId, disabled, disabledReason } = props;
  const [isOpen, setIsOpen] = useState(false);
  const options = getTypeOptions(question);
  const currentTypeOption = getTypeOption(question);

  return (
    <span className="relative inline-flex">
      <button
        type="button"
        className="inline-flex items-center gap-1 text-[12px] font-medium text-muted-foreground transition-colors hover:text-foreground"
        onClick={() => setIsOpen((current) => !current)}
        disabled={disabled}
        title={disabled ? disabledReason : undefined}
        data-testid={`questionnaire-type-button-${questionId}`}
      >
        {typeLabel(question)}
        <ChevronDown className="h-3 w-3 shrink-0 opacity-50" />
      </button>
      {isOpen && !disabled ? (
        <div
          className="absolute left-0 top-6 z-30 w-52 rounded-[8px] border border-border bg-background p-1.5 shadow-[0_6px_24px_rgba(7,18,15,0.10)]"
          data-testid={`questionnaire-type-menu-${questionId}`}
        >
          {options.map((option) => (
            <button
              key={option.id}
              type="button"
              className="flex w-full items-center justify-between rounded-[6px] px-2.5 py-[7px] text-left text-[13px] text-foreground transition-colors hover:bg-muted"
              onClick={() => {
                onChange(option.id);
                setIsOpen(false);
              }}
            >
              <span>{option.label}</span>
              {option.badgeLabel === currentTypeOption.badgeLabel ? (
                <Check className="h-3.5 w-3.5" />
              ) : null}
            </button>
          ))}
        </div>
      ) : null}
    </span>
  );
}

function RequiredPill(props: {
  question: CanonicalEditableQuestion;
  onUpdate: (
    updater: (question: CanonicalEditableQuestion) => CanonicalEditableQuestion,
  ) => void;
}) {
  const { question, onUpdate } = props;
  const isRequired = !("required" in question) || Boolean(question.required);
  return (
    <button
      type="button"
      className={cn(
        PILL_BASE_CLASS,
        "border-border bg-[#F8F8F8] text-muted-foreground dark:bg-muted",
      )}
      onClick={() => {
        if (!("required" in question)) return;
        onUpdate((currentQuestion) => ({
          ...currentQuestion,
          required: !(currentQuestion as any).required,
        }));
      }}
    >
      {isRequired ? "Required" : "Optional"}
    </button>
  );
}

/**
 * Whether option-order randomization is ON for a single_select or
 * multi_select question: only an explicit `randomizeOptions: true`. Absent
 * means false, the stored default the normaliser writes and the compiler
 * reads (`choicesOrder: 'none'`), for either kind.
 */
function randomizeOptionsIsOn(response: {
  randomizeOptions?: boolean;
}): boolean {
  return response.randomizeOptions === true;
}

interface RandomizePillProps {
  isOn: boolean;
  /** A grid shuffles its rows; a list its options. */
  rows: boolean;
  onToggle: () => void;
  disabled?: boolean;
  disabledReason?: string;
  questionId: string;
}

function RandomizePill(props: RandomizePillProps) {
  const { isOn, rows, onToggle, disabled, disabledReason, questionId } = props;
  return (
    <TooltipProvider delayDuration={300}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className={cn(
              PILL_BASE_CLASS,
              "border-primary/25 bg-primary/10 text-foreground",
            )}
            onClick={onToggle}
            disabled={disabled}
            title={disabled ? disabledReason : undefined}
            aria-pressed={isOn}
            data-testid={`questionnaire-question-randomize-${questionId}`}
          >
            Randomize
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-[220px]">
          <span className="text-[11px]">
            {rows
              ? "Show rows in random order per respondent"
              : "Show options in random order per respondent"}
          </span>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

const OUTCOME_CHIP_CLASS =
  "shrink-0 rounded-full px-1.5 py-0 text-[10.5px] font-medium leading-[1.6]";

/**
 * Read-only chip on an option whose pick triggers the host's routing rule
 * (D5): red Disqualified, muted Ends survey / Lands on. Nothing when the
 * option has no derived outcome.
 */
function ChoiceOutcomeChip(props: {
  outcome: RoutingOptionOutcome | undefined;
  testId: string;
}) {
  const { outcome, testId } = props;

  if (!outcome) {
    return null;
  }
  if (outcome.kind === "disqualified") {
    return (
      <Badge
        variant="soft-destructive"
        className={OUTCOME_CHIP_CLASS}
        data-testid={testId}
      >
        {i18n.t("questionnaire.logic.chipDisqualified", { ns: "app" })}
      </Badge>
    );
  }
  return (
    <span
      className={cn(
        OUTCOME_CHIP_CLASS,
        "border border-transparent bg-muted text-muted-foreground",
      )}
      data-testid={testId}
    >
      {outcome.kind === "ends_survey"
        ? i18n.t("questionnaire.logic.chipEndsSurvey", { ns: "app" })
        : i18n.t("questionnaire.logic.chipSkipsTo", {
            ns: "app",
            target: outcome.targetLabel,
          })}
    </span>
  );
}

/**
 * Why an answer's label is not edited here, said inline once the user clicks
 * it: the catalogued line, as a link to the card of the asset it stands for,
 * where it is renamed (C23-e).
 */
function LockedLabelLine(props: {
  reason: string;
  assetRefs: ChoiceOption["assetRefs"];
  testId: string;
}) {
  const { reason, assetRefs, testId } = props;
  const assetId = assetRefs?.find((ref) => ref.role === "choice")?.assetId;
  const className =
    "mb-1 ml-6 self-start text-left text-[12px] leading-[1.5] text-muted-foreground";
  return assetId ? (
    <button
      type="button"
      className={cn(
        className,
        "underline underline-offset-2 hover:text-foreground",
      )}
      onClick={() => revealAssetCard(assetId)}
      data-testid={testId}
    >
      {reason}
    </button>
  ) : (
    <p className={className} data-testid={testId}>
      {reason}
    </p>
  );
}

function ChoiceEditor(props: {
  conceptAssets: Array<ConceptAsset>;
  mentionTables: AssetMentionTables;
  items: Array<ChoiceOption>;
  mode: "radio" | "checkbox";
  /** Absent when nothing can be added (a comparison that has its opt-out). */
  onAdd?: () => void;
  onChange: (index: number, value: string) => void;
  onRemove: (index: number) => void;
  /** The leading options the author drags into order (`authorOrderedOptionCount`). */
  sortableCount: number;
  onMove: (from: number, to: number) => void;
  /** Why an option cannot be removed, when the contract refuses it. */
  removalRefusal: (index: number) => string | undefined;
  /** Why an option's label is not edited here (a typed roster's asset answer). */
  labelRefusal: (index: number) => string | undefined;
  onToggleExclusive?: (index: number, exclusive: boolean) => void;
  /** Whether the contract accepts this option as exclusive. */
  exclusiveOffered: (index: number) => boolean;
  onToggleKeepLast?: (index: number, keepLast: boolean) => void;
  /** Whether "catch-all" (`keepLast`) is offered for this option. */
  keepLastOffered: (index: number) => boolean;
  readOnly?: boolean;
  testIdPrefix: string;
  /** The study's content language, the one respondents read: the blank-option hint. */
  contentLanguage?: string;
  /** Read-only routing outcome per option value (D5 chips). */
  outcomeByValue?: Map<string, RoutingOptionOutcome>;
}) {
  const {
    conceptAssets,
    mentionTables,
    items,
    mode,
    onAdd,
    onChange,
    onRemove,
    sortableCount,
    onMove,
    removalRefusal,
    labelRefusal,
    onToggleExclusive,
    exclusiveOffered,
    onToggleKeepLast,
    keepLastOffered,
    readOnly = false,
    testIdPrefix,
    contentLanguage,
    outcomeByValue,
  } = props;
  // The locked answer whose reason the user asked for by clicking it.
  const [lockShownAt, setLockShownAt] = useState<number | null>(null);

  const draggable = !readOnly && sortableCount >= 2;

  return (
    <div className="flex flex-col">
      <SortableList
        idPrefix={testIdPrefix}
        count={draggable ? sortableCount : 0}
        onMove={onMove}
      >
        {items.map((item, itemIndex) => {
          const lockReason = labelRefusal(itemIndex);
          return (
            <SortableItem
              key={`${testIdPrefix}-${itemIndex}`}
              idPrefix={testIdPrefix}
              index={itemIndex}
              count={draggable ? sortableCount : 0}
              handleLabel={i18n.t("questionnaire.dragToReorder", { ns: "app" })}
              handleTestId={`${testIdPrefix}-${itemIndex}-drag`}
            >
              {({ setNodeRef, style, handle }) => (
                <Fragment>
                  <div
                    ref={setNodeRef}
                    style={style}
                    className="group/choice flex items-center gap-2.5 rounded-[6px] px-1 py-1 transition-colors hover:bg-muted/40 dark:hover:bg-white/5"
                  >
                    {/* A pinned option keeps the grip's width, so the list stays aligned. */}
                    {draggable
                      ? (handle ?? <span aria-hidden className="w-[14px] shrink-0" />)
                      : null}
                    <span
                      className={cn(
                        "h-3.5 w-3.5 shrink-0 border-[1.5px] border-border bg-transparent",
                        mode === "radio" ? "rounded-full" : "rounded-[3px]",
                      )}
                    />
                    <div
                      className="min-w-0 flex-1"
                      onClick={lockReason ? () => setLockShownAt(itemIndex) : undefined}
                    >
                      <ConceptMentionField
                        value={item.label}
                        onChange={(value) => onChange(itemIndex, value)}
                        conceptAssets={conceptAssets}
                        mentionTables={mentionTables}
                        contentLanguage={contentLanguage}
                        boundAssetId={soleAssetId(item.assetRefs)}
                        rows={1}
                        singleLine
                        disabled={readOnly || Boolean(lockReason)}
                        title={lockReason}
                        placeholder={optionPlaceholderLabel(contentLanguage, itemIndex + 1)}
                        className="h-auto min-h-[22px] flex-1 resize-none overflow-hidden border-0 bg-transparent px-0 py-0 text-[13.5px] leading-[1.5] text-foreground shadow-none focus-visible:ring-0"
                        data-testid={`${testIdPrefix}-${itemIndex}`}
                      />
                    </div>
                    <ChoiceOutcomeChip
                      outcome={outcomeByValue?.get(item.value)}
                      testId={`${testIdPrefix}-${itemIndex}-outcome`}
                    />
                    {!readOnly &&
                    mode === "checkbox" &&
                    onToggleExclusive &&
                    exclusiveOffered(itemIndex) ? (
                      <button
                        type="button"
                        className={cn(
                          "shrink-0 rounded-[4px] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide transition-[opacity,background-color,color]",
                          item.exclusive
                            ? "bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300"
                            : "text-muted-foreground/0 hover:bg-muted hover:text-muted-foreground group-hover/choice:text-muted-foreground/70 group-focus-within/choice:text-muted-foreground/70",
                        )}
                        onClick={() => onToggleExclusive(itemIndex, !item.exclusive)}
                        aria-pressed={Boolean(item.exclusive)}
                        aria-label={
                          item.exclusive
                            ? "Exclusive option — selecting it clears all others. Click to unset."
                            : "Mark as an exclusive opt-out option"
                        }
                        title={i18n.t("questionnaire.exclusiveTitle", { ns: "app" })}
                        data-testid={`${testIdPrefix}-${itemIndex}-exclusive`}
                      >
                        Exclusive
                      </button>
                    ) : null}
                    {!readOnly && onToggleKeepLast && keepLastOffered(itemIndex) ? (
                      <button
                        type="button"
                        className={cn(
                          "shrink-0 rounded-[4px] px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide transition-[opacity,background-color,color]",
                          item.keepLast
                            ? "bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300"
                            : "text-muted-foreground/0 hover:bg-muted hover:text-muted-foreground group-hover/choice:text-muted-foreground/70 group-focus-within/choice:text-muted-foreground/70",
                        )}
                        onClick={() => onToggleKeepLast(itemIndex, !item.keepLast)}
                        aria-pressed={Boolean(item.keepLast)}
                        title={i18n.t("questionnaire.keepLastTitle", { ns: "app" })}
                        data-testid={`${testIdPrefix}-${itemIndex}-keep-last`}
                      >
                        {i18n.t("questionnaire.keepLast", { ns: "app" })}
                      </button>
                    ) : null}
                    {!readOnly ? (
                      <button
                        type="button"
                        className="flex h-[20px] w-[20px] shrink-0 items-center justify-center rounded-[4px] text-muted-foreground/0 transition-[opacity,background-color,color] hover:bg-muted hover:text-foreground group-hover/choice:text-muted-foreground group-focus-within/choice:text-muted-foreground disabled:!text-transparent"
                        onClick={() => onRemove(itemIndex)}
                        disabled={items.length <= 2 || Boolean(removalRefusal(itemIndex))}
                        title={removalRefusal(itemIndex)}
                        aria-label={i18n.t("questionnaire.removeOption", { ns: "app" })}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    ) : null}
                  </div>
                  {lockReason && lockShownAt === itemIndex ? (
                    <LockedLabelLine
                      reason={lockReason}
                      assetRefs={item.assetRefs}
                      testId={`${testIdPrefix}-${itemIndex}-locked`}
                    />
                  ) : null}
                </Fragment>
              )}
            </SortableItem>
          );
        })}
      </SortableList>
      {!readOnly && onAdd ? (
        <button
          type="button"
          className="mt-1 inline-flex items-center gap-1.5 self-start rounded-[4px] px-1 py-1 text-[12.5px] text-muted-foreground/60 transition-colors hover:text-muted-foreground focus-visible:text-muted-foreground"
          onClick={onAdd}
        >
          <Plus className="h-3 w-3" />
          Add option
        </button>
      ) : null}
    </div>
  );
}

function RankingEditor(props: {
  conceptAssets: Array<ConceptAsset>;
  mentionTables: AssetMentionTables;
  items: Array<ChoiceOption>;
  minRankedItems: number | undefined;
  maxRankedItems: number | undefined;
  /** Absent when no option can be added: a ranked item has no exclusive escape (D0a). */
  onAdd?: () => void;
  onChange: (index: number, value: string) => void;
  onRemove: (index: number) => void;
  /** The leading items the author drags into order (`authorOrderedOptionCount`). */
  sortableCount: number;
  onMove: (from: number, to: number) => void;
  /** Why an option cannot be removed, when the contract refuses it. */
  removalRefusal: (index: number) => string | undefined;
  /** Why an item's label is not edited here (a typed roster's asset answer). */
  labelRefusal: (index: number) => string | undefined;
  onMinChange: (value: number | undefined) => void;
  onMaxChange: (value: number | undefined) => void;
  readOnlyOptions?: boolean;
  disabled?: boolean;
  testIdPrefix: string;
  /** The study's content language, the one respondents read: the blank-option hint. */
  contentLanguage?: string;
}) {
  const { conceptAssets, mentionTables, items, minRankedItems, maxRankedItems, onAdd, onChange, onRemove, sortableCount, onMove, removalRefusal, labelRefusal, onMinChange, onMaxChange, readOnlyOptions = false, disabled, testIdPrefix, contentLanguage } = props;
  // The locked item whose reason the user asked for by clicking it.
  const [lockShownAt, setLockShownAt] = useState<number | null>(null);
  const draggable = !readOnlyOptions && !disabled && sortableCount >= 2;

  return (
    <div className="flex flex-col">
      <div className="relative flex flex-col gap-0.5">
        <SortableList
          idPrefix={testIdPrefix}
          count={draggable ? sortableCount : 0}
          onMove={onMove}
        >
          {items.map((item, itemIndex) => {
            const lockReason = labelRefusal(itemIndex);
            return (
              <SortableItem
                key={`${testIdPrefix}-${itemIndex}`}
                idPrefix={testIdPrefix}
                index={itemIndex}
                count={draggable ? sortableCount : 0}
                handleLabel={i18n.t("questionnaire.dragToReorder", { ns: "app" })}
                handleTestId={`${testIdPrefix}-${itemIndex}-drag`}
              >
                {({ setNodeRef, style, handle }) => (
                  <Fragment>
                    <div
                      ref={setNodeRef}
                      style={style}
                      className="group/rank relative flex items-center gap-2.5 rounded-[7px] px-1 py-1.5 transition-colors hover:bg-muted/40 dark:hover:bg-white/5"
                    >
                      {handle ?? (
                        <GripVertical className="h-3.5 w-3.5 shrink-0 text-muted-foreground/30 transition-colors group-hover/rank:text-muted-foreground/60" />
                      )}
                      <div
                        className="min-w-0 flex-1"
                        onClick={lockReason ? () => setLockShownAt(itemIndex) : undefined}
                      >
                        <ConceptMentionField
                          value={item.label}
                          onChange={(value) => onChange(itemIndex, value)}
                          conceptAssets={conceptAssets}
                          mentionTables={mentionTables}
                          contentLanguage={contentLanguage}
                          boundAssetId={soleAssetId(item.assetRefs)}
                          rows={1}
                          singleLine
                          disabled={
                            readOnlyOptions || Boolean(disabled) || Boolean(lockReason)
                          }
                          title={lockReason}
                          placeholder={optionPlaceholderLabel(contentLanguage, itemIndex + 1)}
                          className="h-auto min-h-[22px] flex-1 resize-none overflow-hidden border-0 bg-transparent px-0 py-0 text-[13.5px] leading-[1.5] text-foreground shadow-none focus-visible:ring-0"
                          data-testid={`${testIdPrefix}-${itemIndex}`}
                        />
                      </div>
                      {!readOnlyOptions ? (
                        <button
                          type="button"
                          className="flex h-[20px] w-[20px] shrink-0 items-center justify-center rounded-[4px] text-transparent transition-[opacity,background-color,color] hover:bg-muted hover:text-foreground group-hover/rank:text-muted-foreground group-focus-within/rank:text-muted-foreground disabled:!text-transparent"
                          onClick={() => onRemove(itemIndex)}
                          disabled={
                            disabled ||
                            items.length <= 2 ||
                            Boolean(removalRefusal(itemIndex))
                          }
                          title={removalRefusal(itemIndex)}
                          aria-label={i18n.t("questionnaire.removeOption", { ns: "app" })}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      ) : null}
                    </div>
                    {lockReason && lockShownAt === itemIndex ? (
                      <LockedLabelLine
                        reason={lockReason}
                        assetRefs={item.assetRefs}
                        testId={`${testIdPrefix}-${itemIndex}-locked`}
                      />
                    ) : null}
                  </Fragment>
                )}
              </SortableItem>
            );
          })}
        </SortableList>
      </div>
      {!readOnlyOptions && onAdd ? (
        <button
          type="button"
          className="mt-1 inline-flex items-center gap-1.5 self-start rounded-[4px] px-1 py-1 text-[12.5px] text-muted-foreground/60 transition-colors hover:text-muted-foreground focus-visible:text-muted-foreground"
          disabled={disabled}
          onClick={onAdd}
        >
          <Plus className="h-3 w-3" />
          Add option
        </button>
      ) : null}
      <div className="mt-3 flex items-center gap-4 border-t border-border pt-3">
        <div className="flex items-center gap-2">
          <span className="text-[12px] text-muted-foreground">{i18n.t("questionnaire.minRanked", { ns: "app" })}</span>
          <Input
            type="number"
            min={1}
            max={items.length}
            value={minRankedItems ?? ""}
            placeholder="—"
            className="h-7 w-16 rounded-[5px] px-2 text-[12px]"
            data-testid={`${testIdPrefix}-min-ranked`}
            disabled={disabled}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const raw = e.target.value;
              onMinChange(raw === "" ? undefined : Math.max(1, parseInt(raw, 10)));
            }}
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[12px] text-muted-foreground">{i18n.t("questionnaire.maxRanked", { ns: "app" })}</span>
          <Input
            type="number"
            min={1}
            max={items.length}
            value={maxRankedItems ?? ""}
            placeholder="—"
            className="h-7 w-16 rounded-[5px] px-2 text-[12px]"
            data-testid={`${testIdPrefix}-max-ranked`}
            disabled={disabled}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              const raw = e.target.value;
              onMaxChange(raw === "" ? undefined : Math.max(1, parseInt(raw, 10)));
            }}
          />
        </div>
      </div>
    </div>
  );
}

const SCALE_MID_LABELS: Record<string, string> = {
  en: "Neutral", es: "Neutral", fr: "Neutre", de: "Neutral", pt: "Neutro",
  it: "Neutro", nl: "Neutraal", tr: "Nötr", ru: "Нейтрально", ar: "محايد",
  he: "ניטרלי", hi: "तटस्थ", ja: "中立", zh: "中立", ko: "중립",
};

function resizePointLabels(
  labels: Array<string>,
  nextSpan: number,
): Array<string> {
  if (labels.length >= nextSpan) {
    return labels.slice(0, nextSpan);
  }
  return [
    ...labels,
    ...Array.from({ length: nextSpan - labels.length }, () => ""),
  ];
}

// Collapses a legacy wider-than-5 label array (rating_1_7 drafts predating the
// 5/10-only rule) to the 5-point span the same way the backend coercion does:
// first/middle/last become points 1/3/5. Slicing instead would keep the old
// mid-scale wording as the new scale maximum.
function collapseLegacyLabelsToFive(labels: Array<string>): Array<string> {
  const collapsed = ["", "", "", "", ""];
  collapsed[0] = labels[0] ?? "";
  collapsed[2] = labels[midpointIndex(labels.length)] ?? "";
  collapsed[4] = labels[labels.length - 1] ?? "";
  return collapsed;
}

interface ScaleWording {
  unit?: string;
  anchors?: { minLabel?: string; midpointLabel?: string; maxLabel?: string };
  pointLabels?: Array<string>;
}

type RatingScaleTemplates = ReturnType<typeof ratingScaleTemplatesFor>;

function scaleTemplateKeys(
  templates: RatingScaleTemplates,
): Array<RatingScaleTemplateKey> {
  return Object.keys(templates) as Array<RatingScaleTemplateKey>;
}

/**
 * The dictionary template a scale's wording currently matches, or undefined for
 * custom wording. Worded 5pt scales match on their point labels; anchors-only
 * scales (any length) match on min/mid/max. Shared by the single-rating editor
 * and the per-measure matrix scale editor so both name a scale the same way.
 */
function scaleTemplateKey(
  scale: ScaleWording,
  templates: RatingScaleTemplates,
): RatingScaleTemplateKey | undefined {
  const pointLabels = Array.isArray(scale.pointLabels)
    ? scale.pointLabels
    : null;
  if (pointLabels) {
    return scaleTemplateKeys(templates).find((templateKey) => {
      const templateLabels = templates[templateKey].labels;
      return (
        templateLabels.length === pointLabels.length &&
        templateLabels.every((label, index) => label === pointLabels[index])
      );
    });
  }
  return scaleTemplateKeys(templates).find((templateKey) => {
    const templateLabels = templates[templateKey].labels;
    const midpoint = ratingMidpointLabel(
      scale.unit,
      scale.anchors?.midpointLabel,
    );
    return (
      scale.anchors?.minLabel === templateLabels[0] &&
      scale.anchors?.maxLabel === templateLabels[4] &&
      (!midpoint || midpoint === templateLabels[2])
    );
  });
}

/**
 * Custom wording keeps the points numeric and words only the endpoints. A
 * worded scale folds its first/middle/last labels into min/mid/max so picking
 * Custom never blanks what the author already wrote.
 */
function scaleWithCustomWording<T extends ScaleWording>(scale: T): T {
  const pointLabels = Array.isArray(scale.pointLabels)
    ? scale.pointLabels
    : null;
  if (!pointLabels) {
    return scale;
  }
  const { pointLabels: _dropped, ...rest } = scale;
  const mid = midpointIndex(pointLabels.length);
  const last = pointLabels.length - 1;
  return {
    ...rest,
    anchors: {
      ...(scale.anchors || {}),
      minLabel: pointLabels[0]?.trim() || scale.anchors?.minLabel || "",
      midpointLabel: ratingMidpointLabel(
        scale.unit,
        pointLabels[mid]?.trim() || scale.anchors?.midpointLabel || "",
      ),
      maxLabel: pointLabels[last]?.trim() || scale.anchors?.maxLabel || "",
    },
  } as T;
}

/** Focus the field that just mounted. Layout has committed it. */
function focusFieldByTestId(testId: string) {
  if (typeof document === "undefined") {
    return;
  }
  // `CSS` in this module is the dnd-kit helper, which has no `escape`.
  // The browser CSSOM API lives on globalThis.
  const escapeIdent = globalThis.CSS?.escape;
  const escaped =
    typeof escapeIdent === "function"
      ? escapeIdent(testId)
      : testId.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
  document
    .querySelector<HTMLElement>(`[data-testid="${escaped}"]`)
    ?.focus();
}

/** Focus after the wording switch has rendered the inputs it reveals. */
function focusOnNextFrame(focus: () => void) {
  if (typeof window === "undefined") {
    return;
  }
  if (window.requestAnimationFrame) {
    window.requestAnimationFrame(focus);
    return;
  }
  window.setTimeout(focus, 0);
}

interface MeasureScaleEditorProps {
  language?: string;
  measure: ScaleWording & { unit?: string };
  onChange: (patch: (measure: any) => any) => void;
  testId: string;
  disabled?: boolean;
  disabledReason?: string;
}

/**
 * Per-column scale wording for one matrix measure. Every measure already
 * carries its own unit/anchors/pointLabels and compiles to an independent
 * matrixdropdown column, so this edits only the measure it is handed. Wording
 * follows the same rule as the single-rating editor: a dictionary template
 * words all five points, Custom keeps the points numeric and words only the
 * endpoints, plus the midpoint where the measure's unit has one (the compiler
 * prints a measure's midpoint only then, `ratingMidpointLabel`).
 */
function MeasureScaleEditor(props: MeasureScaleEditorProps) {
  const { language, measure, onChange, testId, disabled, disabledReason } =
    props;
  const [wordingIsCustom, setWordingIsCustom] = useState(false);

  const templates = ratingScaleTemplatesFor(language || "en");
  const templateKeys = scaleTemplateKeys(templates);
  const activeTemplateKey = scaleTemplateKey(measure ?? {}, templates);
  // Custom is the author's declared intent, not a derived state: folding a
  // template's labels into anchors leaves wording that still matches that
  // template, so deriving it alone would snap the picker straight back.
  const isCustom = wordingIsCustom || !activeTemplateKey;
  const pointLabels = Array.isArray(measure?.pointLabels)
    ? measure.pointLabels
    : null;
  const minValue = pointLabels
    ? pointLabels[0] ?? ""
    : measure?.anchors?.minLabel ?? "";
  const maxValue = pointLabels
    ? pointLabels[pointLabels.length - 1] ?? ""
    : measure?.anchors?.maxLabel ?? "";

  const applyTemplate = (templateKey: RatingScaleTemplateKey) => {
    setWordingIsCustom(false);
    onChange((current) => {
      const nextLabels = [...templates[templateKey].labels];
      if (storedRatingPoints(current.unit).length > 5) {
        // Longer scales stay numeric: the template words the endpoints only.
        // Stale point labels have to go with them, or the cells would keep
        // rendering wording from the scale the author just replaced.
        const { pointLabels: _dropped, ...rest } = current;
        return {
          ...rest,
          anchors: {
            ...(current.anchors || {}),
            minLabel: nextLabels[0],
            midpointLabel: ratingMidpointLabel(current.unit, nextLabels[2]),
            maxLabel: nextLabels[4],
          },
        };
      }
      return { ...current, pointLabels: nextLabels };
    });
  };

  const applyCustomWording = () => {
    setWordingIsCustom(true);
    onChange((current) => scaleWithCustomWording(current));
    focusOnNextFrame(() => {
      const minInput = document.querySelector<HTMLInputElement>(
        `[data-testid="${testId}-min"]`,
      );
      minInput?.focus();
    });
  };

  const setEndpointLabel = (edge: "min" | "max", value: string) =>
    onChange((current) => {
      const currentLabels = Array.isArray(current.pointLabels)
        ? (current.pointLabels as Array<string>)
        : null;
      if (currentLabels) {
        const nextLabels = [...currentLabels];
        nextLabels[edge === "min" ? 0 : nextLabels.length - 1] = value;
        return { ...current, pointLabels: nextLabels };
      }
      return {
        ...current,
        anchors: {
          ...(current.anchors || {}),
          [edge === "min" ? "minLabel" : "maxLabel"]: value,
        },
      };
    });

  const pickerOptions = [
    ...templateKeys.map((templateKey) => ({
      id: templateKey,
      label: templates[templateKey].label,
    })),
    { id: "custom", label: "Custom" },
  ];
  const pickerLabel =
    activeTemplateKey && !wordingIsCustom
      ? templates[activeTemplateKey].label
      : "Custom";
  const selectedOptionId = isCustom ? "custom" : activeTemplateKey;

  return (
    <MatrixColumnScale
      pickerLabel={pickerLabel}
      pickerOptions={pickerOptions}
      selectedOptionId={selectedOptionId}
      onPick={(id) => {
        if (id === "custom") {
          applyCustomWording();
          return;
        }
        applyTemplate(id as RatingScaleTemplateKey);
      }}
      minValue={minValue}
      maxValue={maxValue}
      onMinChange={(value) => setEndpointLabel("min", value)}
      onMaxChange={(value) => setEndpointLabel("max", value)}
      commitMode="change"
      minPlaceholder={SCALE_MIN_LABELS[language || "en"] || "Low"}
      maxPlaceholder={SCALE_MAX_LABELS[language || "en"] || "High"}
      pickerAriaLabel={i18n.t("questionnaire.scaleWording", { ns: "app" })}
      minAriaLabel={i18n.t("questionnaire.scaleMinLabel", { ns: "app" })}
      maxAriaLabel={i18n.t("questionnaire.scaleMaxLabel", { ns: "app" })}
      disabled={disabled}
      disabledReason={disabledReason}
      testId={testId}
      minTestId={`${testId}-min`}
      maxTestId={`${testId}-max`}
    />
  );
}

function RatingEditor(props: {
  question: CanonicalEditableQuestion;
  onUpdate: (
    updater: (question: CanonicalEditableQuestion) => CanonicalEditableQuestion,
  ) => void;
  questionId: string;
  language?: string;
  /** The study's content language, the one respondents read. */
  contentLanguage?: string;
  disabled?: boolean;
}) {
  const { question, onUpdate, questionId, language, contentLanguage, disabled } =
    props;
  const ratingTemplates = ratingScaleTemplatesFor(language || "en");

  // Positional memory of the user's wording so 10pt->5pt restores their
  // labels instead of blank or template points. Session-local by design: the
  // draft only ever stores exactly one label per visible point.
  const rememberedLabelsRef = useRef<Array<string>>([]);
  // Whether the scale was worded before a switch away from 5 points — a
  // return to 5 restores the worded format only if it was worded before.
  const wasLabeledRef = useRef<boolean>(false);
  const [templateMenuOpen, setTemplateMenuOpen] = useState(false);
  // Custom is declared, not derived: folding a template's labels into anchors
  // leaves wording that still matches that template, so the picker would snap
  // straight back to the template name the author just moved away from.
  const [wordingIsCustom, setWordingIsCustom] = useState(false);
  const minLabelInputRef = useRef<HTMLInputElement>(null);

  const response = question.response as any;
  if (response.kind !== "rating") {
    return null;
  }

  const span = storedRatingPoints(response.unit).length;
  // What respondents pick from: the compiler's options, N/A last when declared.
  const respondentOptions = respondentScaleOptions(
    response,
    respondentWordingFor(contentLanguage),
  );
  const respondentPoints = response.includeNotApplicable
    ? respondentOptions.slice(0, -1)
    : respondentOptions;
  const notApplicable = response.includeNotApplicable
    ? respondentOptions[respondentOptions.length - 1].text
    : "";
  const isLabeled = Array.isArray(response.pointLabels);
  // rating_1_7 is legacy plumbing (builder-coerced to 1-5 on save); it still
  // renders read-only here but gets the 5/10 switch so the user can fix it.
  const showLengthToggle =
    response.unit === "rating_1_5" ||
    response.unit === "rating_1_7" ||
    response.unit === "rating_1_10";
  const midIndex = midpointIndex(span);

  const updateResponse = (patch: (current: any) => any) =>
    onUpdate((currentQuestion) => ({
      ...currentQuestion,
      response: patch(currentQuestion.response as any),
    }));

  const rememberLabels = (labels: Array<string>) => {
    const remembered = [...rememberedLabelsRef.current];
    labels.forEach((label, index) => {
      if (typeof label === "string" && label.trim().length > 0) {
        remembered[index] = label;
      }
    });
    rememberedLabelsRef.current = remembered;
  };

  const withRememberedLabels = (labels: Array<string>) =>
    labels.map((label, index) =>
      label.trim().length > 0
        ? label
        : rememberedLabelsRef.current[index] ?? "",
    );

  // One editor model — no numeric/labeled mode switch. Seeds a per-point
  // label array from the min/mid/max anchors the first time a point gets
  // wording on a question that only had anchors.
  const seededFromAnchors = (current: any, span: number): Array<string> => {
    const seeded = resizePointLabels([], span);
    seeded[0] = current.anchors?.minLabel ?? "";
    seeded[midpointIndex(span)] = current.anchors?.midpointLabel ?? "";
    seeded[span - 1] = current.anchors?.maxLabel ?? "";
    return seeded;
  };

  const setScaleLength = (nextLength: 5 | 10) =>
    updateResponse((current) => {
      const nextUnit = nextLength === 10 ? "rating_1_10" : "rating_1_5";
      const rawLabels = Array.isArray(current.pointLabels)
        ? (current.pointLabels as Array<string>)
        : null;
      // Canonicalize legacy 7-point wording to the 5-span BEFORE remembering
      // or resizing: session memory and resize are positional, so a 7-span
      // array would put mid-scale wording at the wrong points.
      const currentLabels =
        rawLabels && rawLabels.length > 5
          ? collapseLegacyLabelsToFive(rawLabels)
          : rawLabels;
      if (currentLabels) {
        rememberLabels(currentLabels);
      }
      if (nextLength === 10) {
        if (!currentLabels) {
          // An anchors-only scale keeps its endpoints. Its midpoint stays in
          // session memory, at the 5-point middle, for a return to 5 points.
          const midpoint = current.anchors?.midpointLabel;
          if (!midpoint) {
            return { ...current, unit: nextUnit };
          }
          const remembered = resizePointLabels([], 5);
          remembered[midpointIndex(5)] = midpoint;
          rememberLabels(remembered);
          return {
            ...current,
            unit: nextUnit,
            anchors: {
              ...current.anchors,
              midpointLabel: ratingMidpointLabel(nextUnit, midpoint),
            },
          };
        }
        // 10-point scales are numeric: keep the wording as min/mid/max
        // anchors (and in session memory for a return to 5 points).
        wasLabeledRef.current = true;
        const mid = midpointIndex(currentLabels.length);
        const last = currentLabels.length - 1;
        const { pointLabels: _removed, ...rest } = current;
        return {
          ...rest,
          unit: nextUnit,
          anchors: {
            ...(current.anchors || {}),
            minLabel: currentLabels[0]?.trim()
              ? currentLabels[0]
              : current.anchors?.minLabel,
            midpointLabel: ratingMidpointLabel(
              nextUnit,
              currentLabels[mid]?.trim()
                ? currentLabels[mid]
                : current.anchors?.midpointLabel,
            ),
            maxLabel: currentLabels[last]?.trim()
              ? currentLabels[last]
              : current.anchors?.maxLabel,
          },
        };
      }
      if (currentLabels) {
        return {
          ...current,
          unit: nextUnit,
          pointLabels: withRememberedLabels(
            resizePointLabels(currentLabels, 5),
          ),
        };
      }
      if (wasLabeledRef.current) {
        return {
          ...current,
          unit: nextUnit,
          pointLabels: withRememberedLabels(resizePointLabels([], 5)),
        };
      }
      // An anchors-only scale coming back from 10 points gets back the
      // midpoint it had at 5 points (the 10-point scale had none to keep).
      const midpoint = ratingMidpointLabel(
        nextUnit,
        ratingHasMidpoint(current.unit)
          ? current.anchors?.midpointLabel
          : current.anchors?.midpointLabel ||
              rememberedLabelsRef.current[midpointIndex(5)],
      );
      return {
        ...current,
        unit: nextUnit,
        ...(midpoint
          ? { anchors: { ...(current.anchors || {}), midpointLabel: midpoint } }
          : {}),
      };
    });

  const applyCustomWording = () => {
    setTemplateMenuOpen(false);
    setWordingIsCustom(true);
    updateResponse((current) => scaleWithCustomWording(current));
    focusOnNextFrame(() => minLabelInputRef.current?.focus());
  };

  const applyTemplate = (templateKey: RatingScaleTemplateKey) => {
    setTemplateMenuOpen(false);
    setWordingIsCustom(false);
    updateResponse((current) => {
      const nextLabels = [...ratingTemplates[templateKey].labels];
      rememberLabels(nextLabels);
      if (
        storedRatingPoints(current.unit).length > 5 &&
        !Array.isArray(current.pointLabels)
      ) {
        // 10-point and NPS-style scales stay numeric: the template wording
        // lives in the min/mid/max anchors, with the full 5-point wording in
        // session memory so a return to 5 points restores the worded scale.
        // (Legacy 7pt labeled scales keep the pointLabels path below.)
        wasLabeledRef.current = true;
        return {
          ...current,
          anchors: {
            ...(current.anchors || {}),
            minLabel: nextLabels[0],
            midpointLabel: ratingMidpointLabel(current.unit, nextLabels[2]),
            maxLabel: nextLabels[4],
          },
        };
      }
      return { ...current, pointLabels: nextLabels };
    });
  };

  const setPointLabel = (index: number, value: string) =>
    updateResponse((current) => {
      // Legacy tolerance: a not-yet-resaved rating_1_7 question still edits
      // its 7 labels in place (the builder coerces it to 1-5 on next save).
      const nextSpan = current.unit === "rating_1_7" ? 7 : 5;
      const nextLabels = Array.isArray(current.pointLabels)
        ? resizePointLabels(current.pointLabels, nextSpan)
        : seededFromAnchors(current, nextSpan);
      nextLabels[index] = value;
      rememberLabels(nextLabels);
      return { ...current, pointLabels: nextLabels };
    });

  const setAnchor = (
    key: "minLabel" | "midpointLabel" | "maxLabel",
    value: string,
  ) =>
    updateResponse((current) => ({
      ...current,
      anchors: { ...(current.anchors || {}), [key]: value },
    }));

  const segmentButtonClass = (active: boolean) =>
    cn(
      "rounded-[4px] px-2 py-0.5 text-[11px] font-medium transition-colors",
      active
        ? "bg-background text-foreground shadow-sm dark:bg-card"
        : "text-muted-foreground hover:text-foreground",
    );

  // The wording button shows what the scale currently IS, derived from the
  // labels: a dictionary template name when they match one exactly, "Custom"
  // otherwise. Every scale has SOME wording (labels or anchors), so there is
  // no neutral state. Worded 5pt scales match on their full point labels;
  // anchors-only scales (any length) match on min/mid/max anchors.
  const templateKeys = scaleTemplateKeys(ratingTemplates);
  const activeTemplateKey = scaleTemplateKey(response, ratingTemplates);
  const isCustomWording = wordingIsCustom || !activeTemplateKey;
  const wordingButtonLabel =
    activeTemplateKey && !wordingIsCustom
      ? ratingTemplates[activeTemplateKey].label
      : "Custom";

  const controlsElement =
    showLengthToggle || span >= 5 ? (
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          {showLengthToggle ? (
            <div className="inline-flex items-center rounded-[6px] border border-border bg-muted/40 p-0.5">
              {[5, 10].map((length) => (
                <button
                  key={length}
                  type="button"
                  className={segmentButtonClass(
                    (length === 5 && response.unit === "rating_1_5") ||
                      (length === 10 && response.unit === "rating_1_10"),
                  )}
                  onClick={() => setScaleLength(length as 5 | 10)}
                  disabled={disabled}
                  aria-pressed={
                    (length === 5 && response.unit === "rating_1_5") ||
                    (length === 10 && response.unit === "rating_1_10")
                  }
                  data-testid={`questionnaire-rating-length-${length}-${questionId}`}
                >
                  {length} pt
                </button>
              ))}
            </div>
          ) : null}
        </div>
        {span === 5 || (span > 5 && !isLabeled) ? (
          // The same popover the matrix column's wording picker uses: it
          // closes on Escape and on a click outside.
          <Popover
            open={templateMenuOpen && !disabled}
            onOpenChange={setTemplateMenuOpen}
          >
            <PopoverTrigger asChild>
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded-[4px] px-1.5 py-0.5 text-[11px] text-muted-foreground/70 transition-colors hover:bg-muted hover:text-foreground"
                disabled={disabled}
                data-testid={`questionnaire-rating-template-menu-${questionId}`}
              >
                {wordingButtonLabel}
                <ChevronDown className="h-3 w-3" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-44 p-1">
              {templateKeys.map((templateKey) => (
                <button
                  key={templateKey}
                  type="button"
                  className={RATING_TEMPLATE_ITEM_CLASS}
                  onClick={() => applyTemplate(templateKey)}
                  data-testid={`questionnaire-rating-template-${templateKey}-${questionId}`}
                >
                  {ratingTemplates[templateKey].label}
                  {!isCustomWording && templateKey === activeTemplateKey ? (
                    <Check className="h-3 w-3" />
                  ) : null}
                </button>
              ))}
              <button
                type="button"
                className={RATING_TEMPLATE_ITEM_CLASS}
                onClick={applyCustomWording}
                data-testid={`questionnaire-rating-template-custom-${questionId}`}
              >
                Custom
                {isCustomWording ? <Check className="h-3 w-3" /> : null}
              </button>
            </PopoverContent>
          </Popover>
        ) : null}
      </div>
    ) : null;

  const anchorInputClass =
    "h-auto border-0 bg-transparent px-0 py-0 text-[11px] text-muted-foreground shadow-none focus-visible:ring-0";

  // Min / midpoint / max anchor row (Maze-style). For worded scales the
  // anchors ARE the first/middle/last point labels; for numeric scales they
  // edit response.anchors.
  const anchorRowElement = (
    <div className="mt-2 flex items-center gap-4">
      <Input
        ref={minLabelInputRef}
        value={
          isLabeled
            ? response.pointLabels[0] ?? ""
            : response.anchors?.minLabel ||
              SCALE_MIN_LABELS[language || "en"] ||
              ""
        }
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          isLabeled
            ? setPointLabel(0, event.target.value)
            : setAnchor("minLabel", event.target.value)
        }
        className={cn(anchorInputClass, "flex-1 text-left")}
        disabled={disabled}
        data-testid={`questionnaire-rating-min-label-${questionId}`}
      />
      {ratingHasMidpoint(response.unit) ? (
        // The middle label wraps to its whole text, as a grid header does:
        // a long one is never cut (B-UI-MISC U13).
        <Textarea
          rows={1}
          value={
            isLabeled
              ? response.pointLabels[midIndex] ?? ""
              : response.anchors?.midpointLabel || ""
          }
          placeholder={SCALE_MID_LABELS[language || "en"] || "Neutral"}
          onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
            isLabeled
              ? setPointLabel(midIndex, event.target.value)
              : setAnchor("midpointLabel", event.target.value)
          }
          className={cn(
            anchorInputClass,
            "min-h-0 flex-1 resize-none whitespace-normal break-words text-center md:text-[11px] [field-sizing:content]",
          )}
          disabled={disabled}
          data-testid={`questionnaire-rating-mid-label-${questionId}`}
        />
      ) : null}
      <Input
        value={
          isLabeled
            ? response.pointLabels[span - 1] ?? ""
            : response.anchors?.maxLabel ||
              SCALE_MAX_LABELS[language || "en"] ||
              ""
        }
        onChange={(event: ChangeEvent<HTMLInputElement>) =>
          isLabeled
            ? setPointLabel(span - 1, event.target.value)
            : setAnchor("maxLabel", event.target.value)
        }
        className={cn(anchorInputClass, "flex-1 text-right")}
        disabled={disabled}
        data-testid={`questionnaire-rating-max-label-${questionId}`}
      />
    </div>
  );
  return (
    <div>
      {controlsElement}
      <div className="flex gap-1.5">
        {respondentPoints.map(({ value }) => (
          <button
            key={value}
            type="button"
            className="flex h-10 flex-1 items-center justify-center rounded-[6px] border border-border bg-background text-[13px] font-medium text-muted-foreground transition-colors hover:border-accent-highlight hover:text-foreground dark:bg-card"
            disabled={disabled}
          >
            {value}
          </button>
        ))}
        {/* Respondents also see the opt-out choice; the preview shows it too. */}
        {response.includeNotApplicable === true ? (
          <button
            type="button"
            className="flex h-10 min-w-0 max-w-[40%] flex-none items-center justify-center rounded-[6px] border border-border bg-background px-3 text-[12px] font-medium text-muted-foreground dark:bg-card"
            disabled={disabled}
            title={notApplicable}
            data-testid={`questionnaire-rating-not-applicable-${questionId}`}
          >
            <span className="truncate">{notApplicable}</span>
          </button>
        ) : null}
      </div>
      {anchorRowElement}
    </div>
  );
}

const CONTINUOUS_UNIT_OPTIONS: Array<{ value: ContinuousUnitId; label: string }> = [
  { value: "currency", label: "Currency" },
  { value: "percent_0_100", label: "Percent" },
  { value: "duration", label: "Duration" },
  { value: "count", label: "Count" },
  { value: "probability_0_1", label: "Probability" },
  { value: "index_0_100", label: "Index" },
];

const CURRENCY_OPTIONS = [
  { code: "USD", symbol: "$", label: "USD ($)" },
  { code: "EUR", symbol: "€", label: "EUR (€)" },
  { code: "GBP", symbol: "£", label: "GBP (£)" },
  { code: "JPY", symbol: "¥", label: "JPY (¥)" },
  { code: "CAD", symbol: "C$", label: "CAD (C$)" },
  { code: "AUD", symbol: "A$", label: "AUD (A$)" },
  { code: "CHF", symbol: "Fr.", label: "CHF (Fr.)" },
  { code: "MXN", symbol: "MX$", label: "MXN (MX$)" },
  { code: "BRL", symbol: "R$", label: "BRL (R$)" },
];

function getCurrencySymbol(code: string): string {
  return CURRENCY_OPTIONS.find((c) => c.code === code)?.symbol ?? code;
}

function getDefaultMinMax(unit: ContinuousUnitId): [number, number | undefined] {
  const entry: UnitRegistryEntry = CONTINUOUS_UNIT_REGISTRY[unit];
  return [entry.rawMin ?? 0, entry.rawMax];
}

function durationMenuLocale(language: string | undefined): string {
  // `toLocaleUpperCase` rejects underscore tags (`es_MX`). BCP 47 uses hyphens.
  const code = (language ?? "").trim().replace(/_/g, "-");
  return code || "en";
}

function durationMenuLabel(language: string | undefined, unit: string): string {
  const label = durationUnitLabel(language, unit);
  if (!label) {
    return unit;
  }
  return label.charAt(0).toLocaleUpperCase(durationMenuLocale(language)) + label.slice(1);
}

function getContinuousPreview(
  response: ContinuousResponse,
  language: string | undefined,
): {
  prefix: string;
  suffix: string;
  inputPlaceholder: string;
  rangeHint: string;
} {
  const unit = response.unit;
  const [defaultMin, defaultMax] = getDefaultMinMax(unit);
  const min = response.min ?? defaultMin;
  const max = response.max ?? defaultMax;
  const currencyCode = getEffectiveCurrencyCode(response);
  // No unit shown until one is chosen: a missing unit blocks the question.
  const durationLabel = durationUnitLabel(language, response.durationUnit);
  const rangeHint = (format: (value: number) => string): string =>
    continuousRangeHint(
      language,
      format(min),
      max === undefined ? undefined : format(max),
    );

  if (unit === "currency") {
    const symbol = getCurrencySymbol(currencyCode);
    return {
      prefix: symbol,
      suffix: currencyCode,
      inputPlaceholder: "0.00",
      rangeHint: rangeHint((value) => `${symbol}${value}`),
    };
  }
  if (unit === "percent_0_100") {
    return { prefix: "", suffix: "%", inputPlaceholder: "0", rangeHint: rangeHint((value) => `${value}%`) };
  }
  if (unit === "duration") {
    return {
      prefix: "",
      suffix: durationLabel,
      inputPlaceholder: "0",
      rangeHint: rangeHint((value) =>
        durationLabel ? `${value} ${durationLabel}` : String(value),
      ),
    };
  }
  if (unit === "probability_0_1") {
    return { prefix: "", suffix: "", inputPlaceholder: "0.00", rangeHint: rangeHint((value) => value.toFixed(1)) };
  }
  if (unit === "index_0_100") {
    return { prefix: "", suffix: "/ 100", inputPlaceholder: "0", rangeHint: rangeHint(String) };
  }
  return { prefix: "", suffix: "", inputPlaceholder: "0", rangeHint: rangeHint(String) };
}

function ContinuousEditor(props: {
  question: CanonicalEditableQuestion;
  onUpdate: (
    updater: (question: CanonicalEditableQuestion) => CanonicalEditableQuestion,
  ) => void;
  questionId: string;
  /** The study's content language, the one respondents read: unit and range hints. */
  contentLanguage?: string;
  disabled?: boolean;
}) {
  const { question, onUpdate, questionId, contentLanguage, disabled } = props;
  const response = question.response as ContinuousResponse;
  if (response.kind !== "continuous") {
    return null;
  }

  const unit = response.unit;
  const [defaultMin, defaultMax] = getDefaultMinMax(unit);
  // A unit whose registry entry fixes a bound (percent, index, probability)
  // must be authored at exactly that bound: the relations validator refuses
  // any other value. Those bounds are shown read-only, never offered.
  const registry = getUnitRegistryEntry(unit);
  const minIsFixed = typeof registry?.rawMin === "number";
  const maxIsFixed = typeof registry?.rawMax === "number";
  const currencyCode = getEffectiveCurrencyCode(response);
  // A duration without a unit shows none selected: the save does not pick
  // one, and the validator blocks the question until the user does.
  const durationUnit = response.durationUnit ?? "";
  const preview = getContinuousPreview(response, contentLanguage);

  function updateResponse(patch: Partial<ContinuousResponse>) {
    onUpdate((currentQuestion) => ({
      ...currentQuestion,
      response: { ...(currentQuestion.response as ContinuousResponse), ...patch },
    }));
  }

  const selectClass = "h-9 rounded-[7px] border border-border bg-background px-2.5 text-[13px] text-foreground dark:bg-card";

  return (
    <div className="flex flex-col gap-4">
      {/* Single config row: type dropdown + optional extras + min + max */}
      <div className="flex flex-wrap items-end gap-2">
        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-medium text-muted-foreground">{i18n.t("questionnaire.inputType", { ns: "app" })}</label>
          <select
            value={unit}
            onChange={(event: ChangeEvent<HTMLSelectElement>) => {
              const nextUnit = event.target.value as ContinuousUnitId;
              if (nextUnit === unit) return;
              const [min, max] = getDefaultMinMax(nextUnit);
              updateResponse({ unit: nextUnit, min, max });
            }}
            className={`${selectClass} pr-[18px]`}
            disabled={disabled}
            data-testid={`questionnaire-continuous-unit-${questionId}`}
          >
            {CONTINUOUS_UNIT_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {unit === "currency" && (
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-medium text-muted-foreground">{i18n.t("questionnaire.currency", { ns: "app" })}</label>
            <select
              value={currencyCode}
              onChange={(event: ChangeEvent<HTMLSelectElement>) =>
                updateResponse({ currencyCode: event.target.value })
              }
              className={selectClass}
              disabled={disabled}
              data-testid={`questionnaire-continuous-currency-${questionId}`}
            >
              {CURRENCY_OPTIONS.map((c) => (
                <option key={c.code} value={c.code}>{c.label}</option>
              ))}
            </select>
          </div>
        )}

        {unit === "duration" && (
          <div className="flex flex-col gap-1">
            <label className="text-[11px] font-medium text-muted-foreground">{i18n.t("questionnaire.timeUnit", { ns: "app" })}</label>
            <select
              value={durationUnit}
              onChange={(event: ChangeEvent<HTMLSelectElement>) =>
                updateResponse({ durationUnit: event.target.value as DurationUnit })
              }
              className={selectClass}
              disabled={disabled}
              data-testid={`questionnaire-continuous-duration-unit-${questionId}`}
            >
              {durationUnit === "" ? <option value="" disabled hidden /> : null}
              {DURATION_EXPOSED_UNITS.map((du) => (
                <option key={du} value={du}>
                  {durationMenuLabel(contentLanguage, du)}
                </option>
              ))}
            </select>
          </div>
        )}

        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-medium text-muted-foreground">Min</label>
          <Input
            type="number"
            value={String(minIsFixed ? registry?.rawMin : response.min ?? defaultMin)}
            onChange={
              minIsFixed
                ? undefined
                : (event: ChangeEvent<HTMLInputElement>) =>
                    updateResponse({ min: Number(event.target.value) })
            }
            readOnly={minIsFixed}
            className="h-9 w-[86px] rounded-[7px] border-border text-[13.5px]"
            disabled={disabled || minIsFixed}
            data-testid={`questionnaire-continuous-min-${questionId}`}
          />
        </div>

        <div className="flex flex-col gap-1">
          <label className="text-[11px] font-medium text-muted-foreground">Max</label>
          <Input
            type="number"
            value={String(maxIsFixed ? registry?.rawMax : response.max ?? defaultMax ?? "")}
            placeholder={defaultMax === undefined ? "No limit" : undefined}
            onChange={
              maxIsFixed
                ? undefined
                : (event: ChangeEvent<HTMLInputElement>) =>
                    updateResponse({
                      max:
                        event.target.value === ""
                          ? undefined
                          : Number(event.target.value),
                    })
            }
            readOnly={maxIsFixed}
            className="h-9 w-[86px] rounded-[7px] border-border text-[13.5px]"
            disabled={disabled || maxIsFixed}
            data-testid={`questionnaire-continuous-max-${questionId}`}
          />
        </div>
      </div>

      {/* Answer area preview */}
      <div className="flex items-center gap-0 overflow-hidden rounded-[8px] border border-border bg-background dark:bg-card">
        {preview.prefix ? (
          <span className="flex items-center self-stretch border-r border-border bg-muted/40 px-3 text-[13.5px] font-medium text-muted-foreground dark:bg-muted/20">
            {preview.prefix}
          </span>
        ) : null}
        <span className="flex-1 px-3 py-2.5 text-[13.5px] text-muted-foreground/35">
          {preview.inputPlaceholder}
        </span>
        {preview.suffix ? (
          <span className="flex items-center self-stretch border-l border-border bg-muted/40 px-3 text-[13.5px] text-muted-foreground dark:bg-muted/20">
            {preview.suffix}
          </span>
        ) : null}
      </div>
      <p className="-mt-2.5 text-[11.5px] text-muted-foreground/50">
        {preview.rangeHint}
      </p>
    </div>
  );
}

/**
 * Grid columns for a `matrix_rating` question: one column per measure, with
 * the options respondents pick from in that column (`respondentScaleOptions`,
 * the compiler's own list: points, labels, N/A) in the study's language.
 */
function matrixRatingGridColumns(
  columnLabels: Array<string>,
  measures: Array<any>,
  contentLanguage: string | undefined,
): Array<MatrixGridColumn> {
  const wording = respondentWordingFor(contentLanguage);
  return columnLabels.map((label, columnIndex) => {
    const measure = measures[columnIndex];
    return {
      label,
      options: respondentScaleOptions(
        { ...measure, unit: isRatingUnitId(measure?.unit) ? measure.unit : "rating_1_5" },
        wording,
      ).map((option) => ({
        value: String(option.value),
        label: option.text,
      })),
    };
  });
}

/**
 * Per displayed grid row, the asset's author label when the rows are the
 * tested assets (`authorAnswerAssetLabel`: its name, a message's copy, a
 * problem statement, name · price); undefined for author-written rows, whose
 * mentions compile in place. Display only: the row's value stays its `@Name`
 * mention.
 */
function matrixRowRespondentLabels(
  question: CanonicalEditableQuestion,
  conceptAssets: Array<ConceptAsset>,
  rowAssetIds: Array<string | undefined>,
  contentLanguage: string | undefined,
): Array<string | undefined> {
  if ((question.response as any)?.rows?.source !== "tested_assets") {
    return rowAssetIds.map(() => undefined);
  }
  return rowAssetIds.map((assetId) => {
    const asset = conceptAssets.find((concept) => concept.id === assetId);
    return asset
      ? authorAnswerAssetLabel(asset, respondentWordingFor(contentLanguage))
      : undefined;
  });
}

/** Categorical columns are the categories themselves: dots, never a dropdown. */
function matrixCategoricalGridColumns(
  columnLabels: Array<string>,
): Array<MatrixGridColumn> {
  return columnLabels.map((label) => ({ label, options: [] }));
}

function matrixGridRows(rowLabels: Array<string>): Array<MatrixGridRow> {
  return rowLabels.map((label) => ({ label }));
}

const MATRIX_ROW_FIELD_CLASSES =
  "h-auto min-h-[22px] flex-1 resize-none overflow-hidden border-0 bg-transparent px-0 py-0 text-[12.5px] font-medium text-foreground shadow-none focus-visible:ring-0";

/**
 * One grid for every measure count. The 1-measure case used to render a
 * different instrument (a 1..5 value-header row over radio dots, with the scale
 * editor spanned underneath) that never showed `columnLabels` at all, so an
 * author could not see or name the measure. It now renders the same single
 * dropdown column as the multi-measure case, with the measure label as an
 * editable header. A blank label stays blank: the label is the author's, not
 * ours to default.
 */
function MatrixRatingEditor(props: {
  conceptAssets: Array<ConceptAsset>;
  mentionTables: AssetMentionTables;
  question: CanonicalEditableQuestion;
  onUpdate: (
    updater: (question: CanonicalEditableQuestion) => CanonicalEditableQuestion,
  ) => void;
  questionId: string;
  language?: string;
  /** The study's content language, the one respondents read. */
  contentLanguage?: string;
  disabled?: boolean;
  disabledReason?: string;
}) {
  const {
    conceptAssets,
    mentionTables,
    question,
    onUpdate,
    questionId,
    language,
    contentLanguage,
    disabled,
    disabledReason,
  } = props;
  const response = question.response as any;

  if (response.kind !== "matrix_rating") {
    return null;
  }

  const measures = Array.isArray(response.measures) ? response.measures : [];
  const rowLabels = extractRows(question, conceptAssets);
  const rowAssetIds = matrixRowAssetIds(question, conceptAssets);
  const rowRespondentLabels = matrixRowRespondentLabels(
    question,
    conceptAssets,
    rowAssetIds,
    contentLanguage,
  );
  const columnLabels = extractColumns(question);

  const updateRows = (
    edit: (rows: Array<MatrixRow>) => Array<MatrixRowEdit>,
  ) => {
    onUpdate((currentQuestion) =>
      withMatrixRows(currentQuestion, conceptAssets, edit),
    );
  };

  const updateColumns = (
    edit: (
      columns: Array<{ id: string; label: string }>,
    ) => Array<{ id?: string; label: string }>,
  ) => {
    onUpdate((currentQuestion) =>
      withMatrixColumns(currentQuestion, edit, language),
    );
  };

  // Rewrites one measure in place. Measures are addressed by index because the
  // header cells are rendered from the same array; polarity is reconciled on
  // the way out so an edited measure keeps the C3 flag parity invariant.
  const updateMeasure = (
    measureIndex: number,
    patch: (measure: any) => any,
  ) => {
    onUpdate((currentQuestion) => {
      const currentResponse = currentQuestion.response as any;
      const currentMeasures = Array.isArray(currentResponse.measures)
        ? currentResponse.measures
        : [];
      return {
        ...currentQuestion,
        response: {
          ...currentResponse,
          measures: currentMeasures.map((measure: any, index: number) =>
            index === measureIndex ? patch(measure) : measure,
          ),
        },
      };
    });
  };

  return (
    <div>
      <MatrixGrid
        rows={matrixGridRows(rowLabels)}
        columns={matrixRatingGridColumns(columnLabels, measures, contentLanguage)}
        cellKind="dropdown"
        disabled={disabled}
        disabledReason={disabledReason}
        addColumnDisabledReason={i18n.t("questionnaire.maxMatrixColumns", {
          ns: "app",
          max: MAX_MATRIX_MEASURES,
        })}
        minRows={2}
        minColumns={1}
        maxColumns={MAX_MATRIX_MEASURES}
        canEditRows={!disabled}
        minWidthClass="min-w-[560px]"
        revealControlsOnHover
        renderRowLabel={(row, rowIndex) => (
          <ConceptMentionField
            value={row.label}
            onChange={(value) =>
              updateRows((rows) => replaceLabelAt(rows, rowIndex, value))
            }
            conceptAssets={conceptAssets}
            mentionTables={mentionTables}
            contentLanguage={contentLanguage}
            boundAssetId={rowAssetIds[rowIndex]}
            boundAssetLabel={rowRespondentLabels[rowIndex]}
            disabled={disabled}
            rows={1}
            singleLine
            className={MATRIX_ROW_FIELD_CLASSES}
            placeholder={optionPlaceholderLabel(contentLanguage, rowIndex + 1)}
            data-testid={`questionnaire-question-row-${questionId}-${rowIndex}`}
          />
        )}
        renderColumnLabel={(column, columnIndex) => (
          <MatrixHeaderField
            value={column.label}
            onChange={(value) =>
              updateColumns((columns) =>
                replaceLabelAt(columns, columnIndex, value),
              )
            }
            commitMode="change"
            variant="column"
            maxLength={MATRIX_HEADER_MAX_LENGTH}
            disabled={disabled}
            ariaLabel={
              column.label ||
              i18n.t("questionnaire.measureAria", {
                ns: "app",
                index: columnIndex + 1,
              })
            }
            placeholder={i18n.t("questionnaire.measureAria", {
              ns: "app",
              index: columnIndex + 1,
            })}
            dataTestId={`questionnaire-question-column-${questionId}-${columnIndex}`}
          />
        )}
        renderColumnMeta={(_column, columnIndex) => (
          <MeasureScaleEditor
            measure={measures[columnIndex] ?? {}}
            language={language}
            onChange={(patch) => updateMeasure(columnIndex, patch)}
            testId={`questionnaire-question-measure-polarity-${questionId}-${columnIndex}`}
            disabled={disabled}
            disabledReason={disabledReason}
          />
        )}
        cellAriaLabel={(row, column) =>
          `${row.label} ${column.label || "measure"} rating`
        }
        rowTestId={(index) =>
          `questionnaire-question-matrix-row-${questionId}-${index}`
        }
        columnTestId={(index) =>
          `questionnaire-question-matrix-column-${questionId}-${index}`
        }
        cellTestId={(rowIndex, columnIndex) =>
          `questionnaire-question-matrix-cell-${questionId}-${rowIndex}-${columnIndex}`
        }
        addRowTestId={`questionnaire-question-row-add-${questionId}`}
        addColumnTestId={`questionnaire-question-column-add-${questionId}`}
        removeRowTestId={(index) =>
          `questionnaire-question-row-remove-${questionId}-${index}`
        }
        removeColumnTestId={(index) =>
          `questionnaire-question-column-remove-${questionId}-${index}`
        }
        onRowAdd={() => updateRows((rows) => [...rows, { label: "" }])}
        onRowRemove={(index) =>
          updateRows((rows) => withoutItemAt(rows, index))
        }
        onRowMove={rowMove(response, updateRows)}
        moveRowAriaLabel={i18n.t("questionnaire.dragToReorder", { ns: "app" })}
        moveRowTestId={(index) =>
          `questionnaire-question-row-drag-${questionId}-${index}`
        }
        onColumnAdd={() =>
          updateColumns((columns) => [...columns, { label: "" }])
        }
        onColumnRemove={(index) =>
          updateColumns((columns) => withoutItemAt(columns, index))
        }
        addRowLabel={i18n.t("questionnaire.addRow", { ns: "app" })}
        addRowAriaLabel={i18n.t("questionnaire.addRow", { ns: "app" })}
        addColumnAriaLabel={i18n.t("questionnaire.addMeasure", { ns: "app" })}
        removeRowAriaLabel={(index) =>
          i18n.t("questionnaire.removeRowIndexed", {
            ns: "app",
            index: index + 1,
          })
        }
        removeColumnAriaLabel={(index) =>
          i18n.t("questionnaire.removeMeasureIndexed", {
            ns: "app",
            index: index + 1,
          })
        }
      />
    </div>
  );
}

function MatrixCategoricalEditor(props: {
  conceptAssets: Array<ConceptAsset>;
  mentionTables: AssetMentionTables;
  question: CanonicalEditableQuestion;
  onUpdate: (
    updater: (question: CanonicalEditableQuestion) => CanonicalEditableQuestion,
  ) => void;
  questionId: string;
  /** The study's content language, the one respondents read: the blank-row hint. */
  contentLanguage?: string;
  disabled?: boolean;
  disabledReason?: string;
}) {
  const { conceptAssets, mentionTables, question, onUpdate, questionId, contentLanguage, disabled, disabledReason } =
    props;
  const response = question.response as any;
  const rowLabels = extractRows(question, conceptAssets);
  const rowAssetIds = matrixRowAssetIds(question, conceptAssets);
  const rowRespondentLabels = matrixRowRespondentLabels(
    question,
    conceptAssets,
    rowAssetIds,
    contentLanguage,
  );
  const columnLabels = extractColumns(question);

  if (response.kind !== "matrix_categorical") {
    return null;
  }

  const updateRows = (edit: (rows: Array<MatrixRow>) => Array<MatrixRowEdit>) => {
    onUpdate((currentQuestion) =>
      withMatrixRows(currentQuestion, conceptAssets, edit),
    );
  };

  const updateColumns = (
    edit: (
      columns: Array<{ id: string; label: string }>,
    ) => Array<{ id?: string; label: string }>,
  ) => {
    onUpdate((currentQuestion) =>
      withMatrixColumns(currentQuestion, edit, undefined),
    );
  };

  return (
    <div>
      <MatrixGrid
        rows={matrixGridRows(rowLabels)}
        columns={matrixCategoricalGridColumns(columnLabels)}
        cellKind="dot"
        disabled={disabled}
        disabledReason={disabledReason}
        addColumnDisabledReason={i18n.t("questionnaire.maxMatrixColumns", {
          ns: "app",
          max: MAX_MATRIX_CATEGORIES,
        })}
        minRows={2}
        minColumns={2}
        maxColumns={MAX_MATRIX_CATEGORIES}
        canEditRows={!disabled}
        minWidthClass="min-w-[560px]"
        revealControlsOnHover
        renderRowLabel={(row, rowIndex) => (
          <ConceptMentionField
            value={row.label}
            onChange={(value) =>
              updateRows((rows) => replaceLabelAt(rows, rowIndex, value))
            }
            conceptAssets={conceptAssets}
            mentionTables={mentionTables}
            contentLanguage={contentLanguage}
            boundAssetId={rowAssetIds[rowIndex]}
            boundAssetLabel={rowRespondentLabels[rowIndex]}
            disabled={disabled}
            rows={1}
            singleLine
            className={MATRIX_ROW_FIELD_CLASSES}
            placeholder={optionPlaceholderLabel(contentLanguage, rowIndex + 1)}
            data-testid={`questionnaire-question-row-${questionId}-${rowIndex}`}
          />
        )}
        renderColumnLabel={(column, columnIndex) => (
          <MatrixHeaderField
            value={column.label}
            onChange={(value) =>
              updateColumns((columns) =>
                replaceLabelAt(columns, columnIndex, value),
              )
            }
            commitMode="change"
            variant="column"
            maxLength={MATRIX_HEADER_MAX_LENGTH}
            // A typed roster's category is an asset, renamed through it.
            disabled={
              disabled ||
              canonicalEditors.isRosterAnswer(
                response,
                response.categories?.[columnIndex],
              )
            }
            ariaLabel={
              column.label ||
              i18n.t("questionnaire.categoryAria", {
                ns: "app",
                index: columnIndex + 1,
              })
            }
            placeholder={i18n.t("questionnaire.categoryAria", {
              ns: "app",
              index: columnIndex + 1,
            })}
            dataTestId={`questionnaire-question-column-${questionId}-${columnIndex}`}
          />
        )}
        cellAriaLabel={(row, column) => `${row.label} ${column.label}`}
        rowTestId={(index) =>
          `questionnaire-question-matrix-row-${questionId}-${index}`
        }
        columnTestId={(index) =>
          `questionnaire-question-matrix-column-${questionId}-${index}`
        }
        cellTestId={(rowIndex, columnIndex) =>
          `questionnaire-question-matrix-cell-${questionId}-${rowIndex}-${columnIndex}`
        }
        addRowTestId={`questionnaire-question-row-add-${questionId}`}
        addColumnTestId={`questionnaire-question-column-add-${questionId}`}
        removeRowTestId={(index) =>
          `questionnaire-question-row-remove-${questionId}-${index}`
        }
        removeColumnTestId={(index) =>
          `questionnaire-question-column-remove-${questionId}-${index}`
        }
        onRowAdd={() => updateRows((rows) => [...rows, { label: "" }])}
        onRowRemove={(index) =>
          updateRows((rows) => withoutItemAt(rows, index))
        }
        onRowMove={rowMove(response, updateRows)}
        moveRowAriaLabel={i18n.t("questionnaire.dragToReorder", { ns: "app" })}
        moveRowTestId={(index) =>
          `questionnaire-question-row-drag-${questionId}-${index}`
        }
        onColumnAdd={() =>
          updateColumns((columns) => [...columns, { label: "" }])
        }
        onColumnRemove={(index) =>
          updateColumns((columns) => withoutItemAt(columns, index))
        }
        addRowLabel={i18n.t("questionnaire.addRow", { ns: "app" })}
        addRowAriaLabel={i18n.t("questionnaire.addRow", { ns: "app" })}
        addColumnAriaLabel={i18n.t("questionnaire.addCategory", { ns: "app" })}
        removeRowAriaLabel={(index) =>
          i18n.t("questionnaire.removeRowIndexed", {
            ns: "app",
            index: index + 1,
          })
        }
        removeColumnAriaLabel={(index) =>
          i18n.t("questionnaire.removeCategoryIndexed", {
            ns: "app",
            index: index + 1,
          })
        }
      />
    </div>
  );
}

const CHOICE_VISIBILITY_OPERATOR_OPTIONS = [
  { value: "equals", labelKey: "questionnaire.logic.editor.operatorIs" },
  { value: "not_equals", labelKey: "questionnaire.logic.editor.operatorIsNot" },
  { value: "any_of", labelKey: "questionnaire.logic.editor.operatorIsAnyOf" },
  { value: "none_of", labelKey: "questionnaire.logic.editor.operatorIsNoneOf" },
] as const satisfies Array<{
  value: QuestionVisibilityRule["operator"];
  labelKey: string;
}>;

/**
 * The choice operators that read a multi select: its answer is a set, so only
 * the membership ones (`config/questionnaire-condition-operators.json`).
 */
const MULTI_SELECT_VISIBILITY_OPERATOR_OPTIONS =
  CHOICE_VISIBILITY_OPERATOR_OPTIONS.filter((option) =>
    conditionOperatorReads(option.value, "multi_select"),
  );

const NUMERIC_VISIBILITY_OPERATOR_OPTIONS = [
  { value: "eq", labelKey: "questionnaire.logic.editor.operatorIsEqualTo" },
  { value: "gte", labelKey: "questionnaire.logic.editor.operatorIsAtLeast" },
  { value: "gt", labelKey: "questionnaire.logic.editor.operatorIsGreaterThan" },
  { value: "lte", labelKey: "questionnaire.logic.editor.operatorIsAtMost" },
  { value: "lt", labelKey: "questionnaire.logic.editor.operatorIsLessThan" },
  { value: "between", labelKey: "questionnaire.logic.editor.operatorIsBetween" },
] as const satisfies Array<{
  value: QuestionVisibilityRule["operator"];
  labelKey: string;
}>;

const NOT_APPLICABLE_VISIBILITY_OPERATOR_OPTION = {
  value: "is_not_applicable",
  labelKey: "questionnaire.logic.editor.operatorIsNotApplicable",
} as const satisfies {
  value: QuestionVisibilityRule["operator"];
  labelKey: string;
};

const RANKING_VISIBILITY_OPERATOR_OPTIONS = [
  { value: "rank_equals", labelKey: "questionnaire.logic.editor.operatorIsRankedAt" },
  { value: "rank_in_top", labelKey: "questionnaire.logic.editor.operatorIsInTop" },
  { value: "rank_in_bottom", labelKey: "questionnaire.logic.editor.operatorIsInBottom" },
] as const satisfies Array<{
  value: QuestionVisibilityRule["operator"];
  labelKey: string;
}>;

/** An operator whose leaf names one or more answers (the registry's `values`). */
function isMultiValueOperator(
  operator: QuestionVisibilityRule["operator"],
): boolean {
  return conditionOperatorValueCount(operator) === "one_or_more";
}

function isNumericVisibilityOperator(
  operator: QuestionVisibilityRule["operator"],
): boolean {
  return conditionOperatorFamily(operator) === "numeric";
}

function isNotApplicableVisibilityOperator(
  operator: QuestionVisibilityRule["operator"],
): boolean {
  return conditionOperatorFamily(operator) === "not_applicable";
}

function isRankingVisibilityOperator(
  operator: QuestionVisibilityRule["operator"],
): boolean {
  return conditionOperatorFamily(operator) === "ranking";
}

/**
 * A new condition on `candidate`, left incomplete: no answer picked and no
 * threshold, so the author picks them. A default that picked one would make
 * a rule silently ("is at least 1" on a 1-5 scale is always true, and a first
 * option carried into "is not" excludes it unasked). Until it is complete the
 * summary shows the gap, and a Save is refused by the validator naming the
 * question (`visibility_rule_values_missing`).
 */
function defaultRuleForCandidate(
  candidate: VisibilityRuleCandidate,
): QuestionVisibilityRule {
  if (candidate.kind === "matrix_rating") {
    const firstRow = candidate.matrixRows?.[0];
    const measures = candidate.matrixMeasures ?? [];
    const firstMeasure = measures[0];
    return {
      dependsOnStableKey: candidate.stableKey,
      operator: "gte",
      matrixRowValue: firstRow?.value,
      ...(measures.length > 1 && firstMeasure
        ? { matrixMeasureId: firstMeasure.id }
        : {}),
    };
  }
  if (candidate.kind === "matrix_categorical") {
    return {
      dependsOnStableKey: candidate.stableKey,
      operator: "equals",
      values: [],
      matrixRowValue: candidate.matrixRows?.[0]?.value,
    };
  }
  if (candidate.kind === "ranking") {
    return {
      dependsOnStableKey: candidate.stableKey,
      operator: "rank_equals",
      values: [],
      rankK: 1,
    };
  }
  if (candidate.kind === "rating" || candidate.kind === "continuous") {
    return {
      dependsOnStableKey: candidate.stableKey,
      operator: "gte",
    };
  }
  return {
    dependsOnStableKey: candidate.stableKey,
    operator: candidate.isMultiSelect ? "any_of" : "equals",
    values: [],
  };
}

/**
 * The source a new condition starts on: the nearest earlier question it can
 * depend on (candidates are in questionnaire order), leaving out `skip`.
 */
function nearestCandidate(
  candidates: Array<VisibilityRuleCandidate>,
  skip: ReadonlySet<string> = new Set(),
): VisibilityRuleCandidate | undefined {
  return [...candidates]
    .reverse()
    .find(
      (candidate) =>
        !skip.has(candidate.stableKey) &&
        candidate.unavailableAtExposure !== true,
    );
}

/**
 * The logic pickers name each question by its Q number and its title, as the
 * rule's summary line does: two questions can share a title (the same trust
 * question before and after exposure). The numbers are the panel's one map.
 */
function withQuestionLabels<T extends { stableKey: string; title: string }>(
  candidates: Array<T>,
  questionLabelByStableKey: Map<string, string> | undefined,
): Array<T> {
  return candidates.map((candidate) => ({
    ...candidate,
    title: questionNumberedTitle(
      questionLabelByStableKey?.get(candidate.stableKey),
      candidate.title,
    ),
  }));
}

const CHOICES_FROM_MODE_OPTIONS = [
  { value: "selected", labelKey: "questionnaire.logic.editor.choicesFromSelectedOnly" },
  { value: "unselected", labelKey: "questionnaire.logic.editor.choicesFromUnselectedOnly" },
  { value: "all", labelKey: "questionnaire.logic.editor.choicesFromAllOptions" },
] as const satisfies Array<{
  value: ChoicesFromRef["mode"];
  labelKey: string;
}>;

/** A new carry-forward from `candidate` starts on its first accepted mode. */
function defaultChoicesFrom(
  candidate: ChoicesFromCandidate,
  target: CanonicalEditableQuestion,
): ChoicesFromRef {
  return {
    sourceStableKey: candidate.stableKey,
    mode: reachableChoicesFromModes(candidate.source, target)[0],
  };
}

/**
 * Writes the reference and the source's options, through the one derive the
 * server runs (`carriedChoiceOptions`). The mode stays one
 * the contract accepts for the question as it now is: re-pointing the source
 * or raising the target's minimum moves an unreachable mode to the first
 * accepted one (`choices_from_cardinality_unreachable`).
 */
function withSyncedChoicesFrom(
  question: CanonicalEditableQuestion,
  choicesFrom: ChoicesFromRef | undefined,
  candidates: Array<ChoicesFromCandidate>,
): CanonicalEditableQuestion {
  if (!choicesFrom) {
    return canonicalEditors.withChoicesFrom(question, undefined);
  }
  const source = candidates.find(
    (candidate) => candidate.stableKey === choicesFrom.sourceStableKey,
  );
  if (!source) {
    return canonicalEditors.withChoicesFrom(question, choicesFrom);
  }
  const reachable = reachableChoicesFromModes(source.source, question);
  const synced: ChoicesFromRef =
    reachable.length > 0 && !reachable.includes(choicesFrom.mode)
      ? { ...choicesFrom, mode: reachable[0] }
      : choicesFrom;
  const response = canonicalEditors.withChoicesFrom(question, synced)
    .response as ResponseDefinition & {
    options?: Array<ChoiceOption>;
    choicesFrom?: ChoicesFromRef;
  };
  // Canonicalized like every other option write; a ranking that reuses a
  // multi-select's list never carries its `exclusive` escape (D0a).
  return {
    ...question,
    response: canonicalEditors.canonicalizeResponse({
      ...response,
      options: carriedChoiceOptions(
        question.stableKey,
        question.type,
        source.options,
        response.options ?? [],
      ),
      choicesFrom: synced,
    } as ResponseDefinition),
  };
}

/**
 * Carry-forward authoring: show options from an earlier select question,
 * filtered by SurveyJS choicesFromQuestionMode (default: selected).
 */
function ChoicesFromEditor(props: {
  candidates: Array<ChoicesFromCandidate>;
  choicesFrom: ChoicesFromRef | undefined;
  target: CanonicalEditableQuestion;
  onChange: (choicesFrom: ChoicesFromRef | undefined) => void;
  questionId: string;
  disabled?: boolean;
}) {
  const { candidates, choicesFrom, target, onChange, questionId, disabled } =
    props;

  // Adding and removing the reference moved to the Settings menu and the
  // collapsible rule row; this editor only edits an existing one.
  if (candidates.length === 0 || !choicesFrom) {
    return null;
  }
  // Only modes the contract accepts for this source and target are offered;
  // the current mode stays listed so the control shows what is stored.
  const source = candidates.find(
    (candidate) => candidate.stableKey === choicesFrom.sourceStableKey,
  );
  const reachable = source
    ? reachableChoicesFromModes(source.source, target)
    : [];
  const modeOptions = CHOICES_FROM_MODE_OPTIONS.filter(
    (option) =>
      option.value === choicesFrom.mode || reachable.includes(option.value),
  );

  return (
    <div
      className="flex flex-col gap-2"
      data-testid={`questionnaire-choices-from-${questionId}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        <Filter className="h-3 w-3 shrink-0 text-muted-foreground" />
        <span className="text-[12px] text-muted-foreground">{i18n.t("questionnaire.optionsFrom", { ns: "app" })}</span>
        <select
          className="h-7 max-w-[220px] rounded-[5px] border border-border bg-background px-2 text-[12px] text-foreground"
          disabled={disabled}
          value={choicesFrom.sourceStableKey}
          onChange={(event) => {
            onChange({
              ...choicesFrom,
              sourceStableKey: event.target.value,
            });
          }}
          data-testid={`questionnaire-choices-from-source-${questionId}`}
        >
          {candidates.map((candidate) => (
            <option key={candidate.stableKey} value={candidate.stableKey}>
              {candidate.title}
            </option>
          ))}
        </select>
        <select
          className="h-7 rounded-[5px] border border-border bg-background px-2 text-[12px] text-foreground"
          disabled={disabled}
          value={choicesFrom.mode}
          onChange={(event) => {
            onChange({
              ...choicesFrom,
              mode: event.target.value as ChoicesFromRef["mode"],
            });
          }}
          data-testid={`questionnaire-choices-from-mode-${questionId}`}
        >
          {modeOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {i18n.t(option.labelKey, { ns: "app" })}
            </option>
          ))}
        </select>
      </div>
      <p className="text-[11px] leading-snug text-muted-foreground">
        {i18n.t("questionnaire.logic.editor.optionsSyncedHelper", {
          ns: "app",
        })}
      </p>
    </div>
  );
}

/**
 * "Ask only if..." skip-logic authoring for a single question. Supports a
 * single leaf or a flat and/or group of leaf conditions. Adding the rule, its
 * unavailable reason and removing it live in the Settings menu and the
 * collapsible rule row; this editor only edits an existing rule.
 */
function VisibilityRuleEditor(props: {
  candidates: Array<VisibilityRuleCandidate>;
  onChange: (rule: QuestionVisibilityRuleExpr | undefined) => void;
  questionId: string;
  rule: QuestionVisibilityRuleExpr | undefined;
  /** Visibility show/hide vs routing when-condition copy. */
  variant?: "visibility" | "routing";
  testIdPrefix?: string;
  /**
   * When set (routing), the primary (first) leaf is forced to this
   * dependsOnStableKey and its dependency picker is hidden. Additional
   * compound leaves may pick any candidate (host or earlier).
   */
  lockDependsOnStableKey?: string;
  /** Routing host: its option in the dependency pickers reads "this question". */
  hostStableKey?: string;
  disabled?: boolean;
}) {
  const {
    candidates,
    onChange,
    questionId,
    rule,
    variant = "visibility",
    testIdPrefix = "questionnaire-visibility-rule",
    lockDependsOnStableKey,
    hostStableKey,
    disabled,
  } = props;

  if (candidates.length === 0 || !rule) {
    return null;
  }

  const join: VisibilityRuleJoin =
    isVisibilityRuleGroup(rule) && rule.join === "or" ? "or" : "and";
  const leaves = getVisibilityRuleLeaves(rule);

  function withPrimaryLocked(
    leaf: QuestionVisibilityRule,
    leafIndex: number,
  ): QuestionVisibilityRule {
    if (!lockDependsOnStableKey || leafIndex !== 0) {
      return leaf;
    }
    return { ...leaf, dependsOnStableKey: lockDependsOnStableKey };
  }

  function commit(
    nextJoin: VisibilityRuleJoin,
    nextLeaves: Array<QuestionVisibilityRule>,
  ) {
    if (nextLeaves.length === 0) {
      onChange(undefined);
      return;
    }
    if (nextLeaves.length === 1) {
      // Single condition: always the host when routing lock is active.
      onChange(withPrimaryLocked(nextLeaves[0], 0));
      return;
    }
    onChange({
      join: nextJoin,
      rules: nextLeaves
        .slice(0, MAX_VISIBILITY_RULE_GROUP_SIZE)
        .map((leaf, index) => withPrimaryLocked(leaf, index)),
    });
  }

  function unusedCandidateForAdd(): VisibilityRuleCandidate | undefined {
    const used = new Set(leaves.map((leaf) => leaf.dependsOnStableKey));
    // An additional compound leaf starts on the nearest unused dependency,
    // other than the routing host when there is another.
    const usedOrHost = new Set(used);
    if (lockDependsOnStableKey) usedOrHost.add(lockDependsOnStableKey);
    return (
      nearestCandidate(candidates, usedOrHost) ??
      nearestCandidate(candidates, used) ??
      // A compound can legitimately test two values, rows, or ranks from the
      // same available answer. Only unavailable-at-exposure sources stay out.
      nearestCandidate(candidates)
    );
  }

  const addCandidate = unusedCandidateForAdd();

  const joinHeadline =
    variant === "routing"
      ? i18n.t("questionnaire.logic.ifLabel", { ns: "app" })
      : i18n.t("questionnaire.logic.askThisQuestionOnlyIf", { ns: "app" });

  return (
    <div
      className="flex flex-col gap-2"
      data-testid={`${testIdPrefix}-editor-${questionId}`}
    >
      {leaves.length >= 2 ? (
        <div className="flex flex-wrap items-center gap-1.5 text-[12px] text-muted-foreground">
          <GitBranch className="h-3 w-3 shrink-0" />
          <span>{joinHeadline}</span>
          <select
            value={join}
            disabled={disabled}
            onChange={(event) => {
              commit(
                event.target.value === "or" ? "or" : "and",
                leaves,
              );
            }}
            className="h-7 rounded-[6px] border border-border bg-background px-2 text-[12px] text-foreground dark:bg-card"
            data-testid={`${testIdPrefix}-join-${questionId}`}
          >
            <option value="and">
              {i18n.t("questionnaire.logic.editor.allTrue", { ns: "app" })}
            </option>
            <option value="or">
              {i18n.t("questionnaire.logic.editor.anyTrue", { ns: "app" })}
            </option>
          </select>
        </div>
      ) : null}
      {leaves.map((leaf, leafIndex) => {
        const lockedPrimary =
          Boolean(lockDependsOnStableKey) && leafIndex === 0;
        return (
          <VisibilityRuleLeafRow
            key={`${leaf.dependsOnStableKey}-${leafIndex}`}
            candidates={candidates}
            canRemove={leaves.length >= 2}
            disabled={disabled}
            hostStableKey={hostStableKey ?? lockDependsOnStableKey}
            leafIndex={leafIndex}
            lockDependsOn={lockedPrimary}
            onChange={(nextLeaf) => {
              const nextLeaves = [...leaves];
              nextLeaves[leafIndex] = withPrimaryLocked(nextLeaf, leafIndex);
              commit(join, nextLeaves);
            }}
            onRemove={() => {
              commit(
                join,
                leaves.filter((_, index) => index !== leafIndex),
              );
            }}
            prefixLabel={
              variant === "routing"
                ? i18n.t("questionnaire.logic.ifLabel", { ns: "app" })
                : i18n.t("questionnaire.logic.askThisQuestionOnlyIf", {
                    ns: "app",
                  })
            }
            questionId={questionId}
            rule={withPrimaryLocked(leaf, leafIndex)}
            showPrefix={leaves.length === 1}
          />
        );
      })}
      {leaves.length < MAX_VISIBILITY_RULE_GROUP_SIZE && addCandidate ? (
        <button
          type="button"
          className="inline-flex items-center gap-1.5 self-start rounded-[4px] px-1 py-1 text-[12px] text-muted-foreground/70 transition-colors hover:text-muted-foreground"
          disabled={disabled}
          onClick={() => {
            commit(join, [
              ...leaves,
              defaultRuleForCandidate(addCandidate),
            ]);
          }}
          data-testid={`${testIdPrefix}-add-another-${questionId}`}
        >
          <Plus className="h-3 w-3" />
          {i18n.t("questionnaire.logic.editor.addCondition", { ns: "app" })}
        </button>
      ) : null}
    </div>
  );
}

/**
 * The first "when" candidate of a new routing rule: the host where supported;
 * an earlier declared source is also valid. Undefined when nothing qualifies,
 * which is when the Settings menu lists routing as unavailable.
 */
function initialRoutingCandidate(
  whenCandidates: Array<VisibilityRuleCandidate>,
  hostStableKey: string,
): VisibilityRuleCandidate | undefined {
  const hostCandidate = whenCandidates.find(
    (candidate) => candidate.stableKey === hostStableKey,
  );
  // Start with the host where supported; an earlier declared source is also valid.
  return hostCandidate ?? whenCandidates[whenCandidates.length - 1];
}

/**
 * The routing rule the Settings menu adds.
 *
 * A skip lands on its target. The immediate next question skips nothing, so
 * the rule starts on the second later question. With nowhere useful to land,
 * the rule ends the survey instead of saving a no-op skip.
 */
function defaultRoutingRule(
  initialCandidate: VisibilityRuleCandidate,
  targetCandidates: Array<RoutingTargetCandidate>,
  hostStableKey: string,
  endSurveyDisposition: "branch_complete" | "screened_out",
): QuestionRoutingRule {
  const targetStableKey = defaultSkipTargetStableKey(targetCandidates);
  const canSkipTo = Boolean(targetStableKey);
  return {
    ruleId: `route:${hostStableKey}`,
    scope: "respondent",
    ...(!canSkipTo ? { disposition: endSurveyDisposition } : {}),
    when: defaultRuleForCandidate(initialCandidate),
    action: canSkipTo ? "skip_to" : "end_survey",
    ...(canSkipTo ? { targetStableKey } : {}),
  };
}

function RoutingRuleEditor(props: {
  whenCandidates: Array<VisibilityRuleCandidate>;
  targetCandidates: Array<RoutingTargetCandidate>;
  hostStableKey: string;
  endSurveyDisposition: "branch_complete" | "screened_out";
  onChange: (rule: QuestionRoutingRule | undefined) => void;
  questionId: string;
  rule: QuestionRoutingRule | undefined;
  disabled?: boolean;
}) {
  const {
    whenCandidates,
    targetCandidates,
    hostStableKey,
    endSurveyDisposition,
    onChange,
    questionId,
    rule,
    disabled,
  } = props;

  // Adding and removing the rule moved to the Settings menu and the
  // collapsible rule row; this editor only edits an existing rule.
  if (!rule) {
    return null;
  }

  const action = rule.action === "end_survey" ? "end_survey" : "skip_to";
  const canLandAhead = Boolean(defaultSkipTargetStableKey(targetCandidates));
  const landsOnImmediateNext = isImmediateNextSkipTarget(
    targetCandidates,
    rule.targetStableKey,
  );
  const selectClass =
    "h-7 rounded-[6px] border border-border bg-background px-2 text-[12px] text-foreground dark:bg-card";

  return (
    <div
      className="flex flex-col gap-2"
      data-testid={`questionnaire-routing-rule-editor-${questionId}`}
    >
      <VisibilityRuleEditor
        disabled={disabled}
        candidates={whenCandidates}
        onChange={(when) => {
          if (!when) {
            onChange(undefined);
            return;
          }
          onChange({
            ...rule,
            when,
          });
        }}
        questionId={questionId}
        rule={rule.when}
        variant="routing"
        testIdPrefix="questionnaire-routing-when"
        hostStableKey={hostStableKey}
      />
      <div className="flex flex-wrap items-center gap-1.5 text-[12px] text-muted-foreground">
        <span>{i18n.t("questionnaire.logic.editor.then", { ns: "app" })}</span>
        <select
          disabled={disabled}
          value={action}
          onChange={(event) => {
            const nextAction =
              event.target.value === "end_survey" ? "end_survey" : "skip_to";
            if (nextAction === "end_survey") {
              onChange({ ...rule, targetStableKey: undefined, scope: "respondent", disposition: endSurveyDisposition, action: "end_survey" });
              return;
            }
            const currentIsUseful =
              Boolean(rule.targetStableKey) &&
              !isImmediateNextSkipTarget(targetCandidates, rule.targetStableKey) &&
              targetCandidates.some(
                (candidate) =>
                  candidate.stableKey === rule.targetStableKey &&
                  candidate.samePass !== false,
              );
            const fallbackTarget = currentIsUseful
              ? rule.targetStableKey
              : defaultSkipTargetStableKey(targetCandidates);
            if (!fallbackTarget) {
              onChange({ ...rule, targetStableKey: undefined, scope: "respondent", disposition: endSurveyDisposition, action: "end_survey" });
              return;
            }
            onChange({
              ...rule,
              disposition: undefined,
              action: "skip_to",
              targetStableKey: fallbackTarget,
            });
          }}
          className={selectClass}
          data-testid={`questionnaire-routing-rule-action-${questionId}`}
        >
          <option value="skip_to" disabled={!canLandAhead}>
            {i18n.t("questionnaire.logic.editor.skipToQuestion", {
              ns: "app",
            })}
          </option>
          <option value="end_survey">
            {i18n.t("questionnaire.logic.editor.endSurveyOption", {
              ns: "app",
            })}
          </option>
        </select>
        {action === "skip_to" ? (
          <select
            disabled={disabled}
            value={rule.targetStableKey ?? ""}
            onChange={(event) => {
              const targetStableKey = event.target.value;
              const chosen = targetCandidates.find(
                (candidate) => candidate.stableKey === targetStableKey,
              );
              if (
                !targetStableKey ||
                isImmediateNextSkipTarget(targetCandidates, targetStableKey) ||
                chosen?.samePass === false
              ) {
                return;
              }
              onChange({
                ...rule,
                disposition: undefined,
                action: "skip_to",
                targetStableKey,
              });
            }}
            className={selectClass}
            data-testid={`questionnaire-routing-rule-target-${questionId}`}
          >
            {!rule.targetStableKey ? (
              <option value="" disabled>
                {i18n.t("questionnaire.logic.editor.chooseLandingQuestion", {
                  ns: "app",
                })}
              </option>
            ) : null}
            {targetCandidates.map((candidate, candidateIndex) => (
              <option
                key={candidate.stableKey}
                value={candidate.stableKey}
                disabled={candidateIndex === 0 || candidate.samePass === false}
              >
                {candidateIndex === 0
                  ? i18n.t(
                      "questionnaire.logic.editor.skipTargetNextSkipsNothing",
                      { ns: "app", title: candidate.title },
                    )
                  : candidate.title}
              </option>
            ))}
          </select>
        ) : null}
      </div>
      {action === "skip_to" ? (
        <span className="flex flex-col gap-1 text-[12px] text-muted-foreground">
          {i18n.t("questionnaire.logic.editor.landsOnHelper", { ns: "app" })}
          {landsOnImmediateNext ? (
            <span
              className="text-destructive"
              data-testid={`questionnaire-routing-rule-noop-${questionId}`}
            >
              {i18n.t("questionnaire.logic.editor.noopSkipWarning", {
                ns: "app",
              })}
            </span>
          ) : null}
        </span>
      ) : null}
      {action === "end_survey" && (
        <label className="text-xs text-muted-foreground">
          {i18n.t("questionnaire.logic.editor.endingType", { ns: "app" })}{" "}
          <select className={selectClass} disabled={disabled} value={rule.disposition ?? "unknown"} data-testid={`questionnaire-routing-rule-disposition-${questionId}`}
            onChange={(event) => onChange({ ...rule, scope: "respondent", disposition: event.target.value as "branch_complete" | "screened_out" })}>
            {!rule.disposition && (
              <option value="unknown" disabled>
                {i18n.t("questionnaire.logic.editor.savedEndingTypeUnknown", {
                  ns: "app",
                })}
              </option>
            )}
            <option value="branch_complete">
              {i18n.t("questionnaire.logic.editor.validJourneyEnding", {
                ns: "app",
              })}
            </option>
            <option value="screened_out">
              {i18n.t("questionnaire.logic.editor.ineligibleRespondent", {
                ns: "app",
              })}
            </option>
          </select>
          <span className="block mt-1">
            {rule.scope === "respondent"
              ? i18n.t("questionnaire.logic.editor.endsAllRemaining", {
                  ns: "app",
                })
              : i18n.t(
                  "questionnaire.logic.editor.existingRuleEndsSession",
                  { ns: "app" },
                )}
          </span>
        </label>
      )}
    </div>
  );
}

function VisibilityRuleLeafRow(props: {
  candidates: Array<VisibilityRuleCandidate>;
  /** Per-condition remove shows only with 2+ conditions (D7). */
  canRemove?: boolean;
  disabled?: boolean;
  /** Its option in the dependency picker reads "this question" (D4). */
  hostStableKey?: string;
  leafIndex: number;
  lockDependsOn?: boolean;
  onChange: (rule: QuestionVisibilityRule) => void;
  onRemove: () => void;
  prefixLabel?: string;
  questionId: string;
  rule: QuestionVisibilityRule;
  showPrefix: boolean;
}) {
  const {
    candidates,
    canRemove = true,
    disabled,
    hostStableKey,
    leafIndex,
    lockDependsOn = false,
    onChange,
    onRemove,
    prefixLabel = i18n.t("questionnaire.logic.askThisQuestionOnlyIf", {
      ns: "app",
    }),
    questionId,
    rule,
    showPrefix,
  } = props;
  const testSuffix =
    leafIndex === 0 ? questionId : `${questionId}-${leafIndex}`;

  const selectedCandidate =
    candidates.find(
      (candidate) => candidate.stableKey === rule.dependsOnStableKey,
    ) ?? candidates[0];
  const isMatrixDependency = selectedCandidate.kind === "matrix_rating";
  const isMatrixCategoricalDependency =
    selectedCandidate.kind === "matrix_categorical";
  const isMatrixCellDependency =
    isMatrixDependency || isMatrixCategoricalDependency;
  const isRankingDependency = selectedCandidate.kind === "ranking";
  const isNumericDependency =
    selectedCandidate.kind === "rating" ||
    selectedCandidate.kind === "continuous" ||
    isMatrixDependency;
  const isRatingDependency = selectedCandidate.kind === "rating";
  const isContinuousDependency = selectedCandidate.kind === "continuous";
  const matrixRows = selectedCandidate.matrixRows ?? [];
  const matrixMeasures = selectedCandidate.matrixMeasures ?? [];
  const selectedMatrixMeasure =
    matrixMeasures.find((measure) => measure.id === rule.matrixMeasureId) ??
    matrixMeasures[0];
  const supportsNotApplicable = isMatrixDependency
    ? selectedMatrixMeasure?.includeNotApplicable === true
    : selectedCandidate.includeNotApplicable === true;
  const maxRank =
    typeof selectedCandidate.maxRank === "number" &&
    selectedCandidate.maxRank >= 1
      ? selectedCandidate.maxRank
      : Math.max(selectedCandidate.options.length, 1);
  const rankingRankK =
    typeof rule.rankK === "number" &&
    Number.isInteger(rule.rankK) &&
    rule.rankK >= 1 &&
    rule.rankK <= maxRank
      ? rule.rankK
      : 1;
  const baseOperatorOptions = isRankingDependency
    ? RANKING_VISIBILITY_OPERATOR_OPTIONS
    : isNumericDependency
      ? NUMERIC_VISIBILITY_OPERATOR_OPTIONS
      : selectedCandidate.isMultiSelect
        ? MULTI_SELECT_VISIBILITY_OPERATOR_OPTIONS
        : CHOICE_VISIBILITY_OPERATOR_OPTIONS;
  const operatorOptions = supportsNotApplicable
    ? [...baseOperatorOptions, NOT_APPLICABLE_VISIBILITY_OPERATOR_OPTION]
    : baseOperatorOptions;
  const multiValue = isMultiValueOperator(rule.operator);
  const ruleValues = Array.isArray(rule.values) ? rule.values : [];
  const selectClass =
    "h-7 rounded-[6px] border border-border bg-background px-2 text-[12px] text-foreground dark:bg-card";
  const ratingThresholdUnit = isMatrixDependency
    ? selectedMatrixMeasure?.unit
    : selectedCandidate.unit;
  const thresholdOptions =
    (isRatingDependency || isMatrixDependency) &&
    typeof ratingThresholdUnit === "string" &&
    isRatingUnitId(ratingThresholdUnit)
      ? // The values respondents can answer: the compiler's scale points.
        respondentScalePoints({ unit: ratingThresholdUnit }).map(
          (point) => point.value as number,
        )
      : [];
  const isBetweenOperator = rule.operator === "between";
  // A threshold is the author's pick, never a default: unset until chosen
  // (see `defaultRuleForCandidate`).
  const ruleThreshold =
    typeof rule.threshold === "number" && Number.isFinite(rule.threshold)
      ? rule.threshold
      : undefined;
  const betweenThresholdHigh =
    typeof rule.thresholdHigh === "number" &&
    Number.isFinite(rule.thresholdHigh)
      ? rule.thresholdHigh
      : undefined;
  /** The upper bound after a new lower one: raised to it when below. */
  function highAfter(nextThreshold: number): number | undefined {
    return typeof betweenThresholdHigh === "number" &&
      betweenThresholdHigh < nextThreshold
      ? nextThreshold
      : betweenThresholdHigh;
  }

  function matrixCellFields(next?: {
    matrixRowValue?: string;
    matrixMeasureId?: string;
    /** `null` unsets it. */
    threshold?: number | null;
    thresholdHigh?: number | null;
    operator?: QuestionVisibilityRule["operator"];
  }): Pick<
    QuestionVisibilityRule,
    "matrixRowValue" | "matrixMeasureId" | "threshold" | "thresholdHigh"
  > {
    const rowValue =
      next?.matrixRowValue ??
      rule.matrixRowValue ??
      matrixRows[0]?.value;
    const measureId =
      matrixMeasures.length > 1
        ? (next?.matrixMeasureId ??
          rule.matrixMeasureId ??
          selectedMatrixMeasure?.id)
        : undefined;
    const operator =
      next?.operator && isNumericVisibilityOperator(next.operator)
        ? next.operator
        : isNumericVisibilityOperator(rule.operator)
          ? rule.operator
          : "gte";
    const threshold =
      next?.threshold === undefined ? ruleThreshold : next.threshold;
    const thresholdHigh =
      next?.thresholdHigh === undefined
        ? betweenThresholdHigh
        : next.thresholdHigh;
    return {
      ...(typeof rowValue === "string" ? { matrixRowValue: rowValue } : {}),
      ...(typeof measureId === "string" ? { matrixMeasureId: measureId } : {}),
      ...(typeof threshold === "number" ? { threshold } : {}),
      ...(operator === "between" && typeof thresholdHigh === "number"
        ? { thresholdHigh }
        : {}),
    };
  }

  function notApplicableFields(): QuestionVisibilityRule {
    return {
      dependsOnStableKey: rule.dependsOnStableKey,
      operator: "is_not_applicable",
      ...(isMatrixDependency
        ? {
            ...(typeof (rule.matrixRowValue ?? matrixRows[0]?.value) === "string"
              ? { matrixRowValue: rule.matrixRowValue ?? matrixRows[0]?.value }
              : {}),
            ...(matrixMeasures.length > 1 &&
            typeof (rule.matrixMeasureId ?? selectedMatrixMeasure?.id) === "string"
              ? { matrixMeasureId: rule.matrixMeasureId ?? selectedMatrixMeasure?.id }
              : {}),
          }
        : {}),
    };
  }

  function plainNumericFields(next?: {
    operator?: QuestionVisibilityRule["operator"];
    threshold?: number;
    thresholdHigh?: number;
  }): QuestionVisibilityRule {
    const operator =
      next?.operator && isNumericVisibilityOperator(next.operator)
        ? next.operator
        : isNumericVisibilityOperator(rule.operator)
          ? rule.operator
          : "gte";
    const threshold = next?.threshold ?? ruleThreshold;
    const thresholdHigh = next?.thresholdHigh ?? betweenThresholdHigh;
    return {
      dependsOnStableKey: rule.dependsOnStableKey,
      operator,
      ...(typeof threshold === "number" ? { threshold } : {}),
      ...(operator === "between" && typeof thresholdHigh === "number"
        ? { thresholdHigh }
        : {}),
    };
  }

  function choiceFields(next?: {
    operator?: QuestionVisibilityRule["operator"];
    values?: Array<string>;
    matrixRowValue?: string;
  }): QuestionVisibilityRule {
    const operator =
      next?.operator && !isNumericVisibilityOperator(next.operator)
        ? next.operator
        : isNumericVisibilityOperator(rule.operator)
          ? "equals"
          : rule.operator;
    return {
      dependsOnStableKey: rule.dependsOnStableKey,
      operator,
      values:
        next?.values ??
        (isMultiValueOperator(operator) ? ruleValues : ruleValues.slice(0, 1)),
      ...(isMatrixCategoricalDependency
        ? {
            matrixRowValue:
              next?.matrixRowValue ?? rule.matrixRowValue ?? matrixRows[0]?.value,
          }
        : {}),
    };
  }

  function rankingFields(next?: {
    operator?: QuestionVisibilityRule["operator"];
    values?: Array<string>;
    rankK?: number;
  }): QuestionVisibilityRule {
    const operator =
      next?.operator && isRankingVisibilityOperator(next.operator)
        ? next.operator
        : isRankingVisibilityOperator(rule.operator)
          ? rule.operator
          : "rank_equals";
    const values =
      next?.values ??
      (isMultiValueOperator(operator) ? ruleValues : ruleValues.slice(0, 1));
    const rankK =
      typeof next?.rankK === "number" ? next.rankK : rankingRankK;
    return {
      dependsOnStableKey: rule.dependsOnStableKey,
      operator,
      values,
      rankK,
    };
  }

  const operatorSelectValue = (() => {
    if (isRankingDependency) {
      return isRankingVisibilityOperator(rule.operator)
        ? rule.operator
        : "rank_equals";
    }
    if (isNumericDependency) {
      return isNumericVisibilityOperator(rule.operator) ||
        (isNotApplicableVisibilityOperator(rule.operator) && supportsNotApplicable)
        ? rule.operator
        : "gte";
    }
    if (
      isNumericVisibilityOperator(rule.operator) ||
      isRankingVisibilityOperator(rule.operator)
    ) {
      return selectedCandidate.isMultiSelect ? "any_of" : "equals";
    }
    if (selectedCandidate.isMultiSelect) {
      // A multi select's answer is a set: a choice operator becomes its
      // membership twin of the same polarity (equals -> any_of).
      return conditionMembershipOperator(rule.operator) ?? "any_of";
    }
    return rule.operator;
  })();

  return (
    <div
      className="flex flex-col gap-2"
      data-testid={`questionnaire-visibility-rule-leaf-${testSuffix}`}
    >
      <div className="flex flex-wrap items-center gap-1.5 text-[12px] text-muted-foreground">
        {showPrefix ? (
          <>
            <GitBranch className="h-3 w-3 shrink-0" />
            <span>{prefixLabel}</span>
          </>
        ) : (
          <span className="text-muted-foreground/70">
            {i18n.t("questionnaire.logic.editor.conditionIndexed", {
              ns: "app",
              index: leafIndex + 1,
            })}
          </span>
        )}
        {lockDependsOn ? (
          <span className="text-foreground">
            {i18n.t("questionnaire.logic.thisQuestion", { ns: "app" })}
          </span>
        ) : (
          <select
            value={selectedCandidate.stableKey}
            disabled={disabled}
            onChange={(event: ChangeEvent<HTMLSelectElement>) => {
              const nextCandidate = candidates.find(
                (candidate) => candidate.stableKey === event.target.value,
              );
              if (!nextCandidate) {
                return;
              }
              onChange(defaultRuleForCandidate(nextCandidate));
            }}
            className={selectClass}
            data-testid={`questionnaire-visibility-rule-depends-on-${testSuffix}`}
          >
            {candidates.map((candidate) => (
              <option
                key={candidate.stableKey}
                value={candidate.stableKey}
                disabled={candidate.unavailableAtExposure === true}
              >
                {hostStableKey && candidate.stableKey === hostStableKey
                  ? i18n.t("questionnaire.logic.thisQuestion", { ns: "app" })
                  : candidate.title}
              </option>
            ))}
          </select>
        )}
        {isMatrixCellDependency ? (
          <select
            value={rule.matrixRowValue ?? matrixRows[0]?.value ?? ""}
            disabled={disabled}
            onChange={(event: ChangeEvent<HTMLSelectElement>) => {
              if (isMatrixCategoricalDependency) {
                onChange(choiceFields({ matrixRowValue: event.target.value }));
                return;
              }
              onChange({
                dependsOnStableKey: rule.dependsOnStableKey,
                operator: isNotApplicableVisibilityOperator(rule.operator)
                  ? rule.operator
                  : isNumericVisibilityOperator(rule.operator)
                  ? rule.operator
                  : "gte",
                ...matrixCellFields({ matrixRowValue: event.target.value }),
              });
            }}
            className={selectClass}
            data-testid={`questionnaire-visibility-rule-matrix-row-${testSuffix}`}
          >
            {matrixRows.map((row) => (
              <option key={row.value} value={row.value}>
                {row.label}
              </option>
            ))}
          </select>
        ) : null}
        {isMatrixDependency && matrixMeasures.length > 1 ? (
          <select
            value={
              rule.matrixMeasureId ?? selectedMatrixMeasure?.id ?? ""
            }
            disabled={disabled}
            onChange={(event: ChangeEvent<HTMLSelectElement>) => {
              const nextMeasure = matrixMeasures.find(
                (measure) => measure.id === event.target.value,
              );
              const keepNotApplicable =
                isNotApplicableVisibilityOperator(rule.operator) &&
                nextMeasure?.includeNotApplicable === true;
              onChange({
                dependsOnStableKey: rule.dependsOnStableKey,
                operator: keepNotApplicable
                  ? "is_not_applicable"
                  : isNumericVisibilityOperator(rule.operator)
                  ? rule.operator
                  : "gte",
                ...matrixCellFields({
                  matrixMeasureId: event.target.value,
                  operator: keepNotApplicable
                    ? "is_not_applicable"
                    : isNumericVisibilityOperator(rule.operator)
                      ? rule.operator
                      : "gte",
                  threshold:
                    !keepNotApplicable &&
                    typeof ruleThreshold === "number" &&
                    nextMeasure &&
                    ruleThreshold >= nextMeasure.rateMin &&
                    ruleThreshold <= nextMeasure.rateMax
                      ? ruleThreshold
                      : null,
                }),
              });
            }}
            className={selectClass}
            data-testid={`questionnaire-visibility-rule-matrix-measure-${testSuffix}`}
          >
            {matrixMeasures.map((measure) => (
              <option key={measure.id} value={measure.id}>
                {measure.label}
              </option>
            ))}
          </select>
        ) : null}
        <select
          value={operatorSelectValue}
          disabled={disabled}
          onChange={(event: ChangeEvent<HTMLSelectElement>) => {
            const nextOperator = event.target
              .value as QuestionVisibilityRule["operator"];
            if (isRankingVisibilityOperator(nextOperator)) {
              onChange(
                rankingFields({
                  operator: nextOperator,
                  values: isMultiValueOperator(nextOperator)
                    ? ruleValues
                    : ruleValues.slice(0, 1),
                }),
              );
              return;
            }
            if (isNumericVisibilityOperator(nextOperator)) {
              if (isMatrixDependency) {
                onChange({
                  dependsOnStableKey: rule.dependsOnStableKey,
                  operator: nextOperator,
                  ...matrixCellFields({ operator: nextOperator }),
                });
                return;
              }
              onChange(plainNumericFields({ operator: nextOperator }));
              return;
            }
            if (isNotApplicableVisibilityOperator(nextOperator)) {
              onChange(notApplicableFields());
              return;
            }
            onChange(
              choiceFields({
                operator: nextOperator,
                values: isMultiValueOperator(nextOperator)
                  ? ruleValues
                  : ruleValues.slice(0, 1),
              }),
            );
          }}
          className={selectClass}
          data-testid={`questionnaire-visibility-rule-operator-${testSuffix}`}
        >
          {operatorOptions.map((option) => (
            <option key={option.value} value={option.value}>
              {i18n.t(option.labelKey, { ns: "app" })}
            </option>
          ))}
        </select>
        {isRankingDependency ? (
          <select
            value={String(rankingRankK)}
            disabled={disabled}
            onChange={(event: ChangeEvent<HTMLSelectElement>) => {
              onChange(
                rankingFields({ rankK: Number(event.target.value) }),
              );
            }}
            className={selectClass}
            data-testid={`questionnaire-visibility-rule-rank-k-${testSuffix}`}
          >
            {Array.from({ length: maxRank }, (_, index) => index + 1).map(
              (rank) => (
                <option key={rank} value={rank}>
                  {rank}
                </option>
              ),
            )}
          </select>
        ) : null}
        {(isRatingDependency || isMatrixDependency) &&
        isNumericVisibilityOperator(rule.operator) ? (
          <>
            <select
              value={
                typeof ruleThreshold === "number" ? String(ruleThreshold) : ""
              }
              disabled={disabled}
              onChange={(event: ChangeEvent<HTMLSelectElement>) => {
                const nextThreshold = Number(event.target.value);
                if (isMatrixDependency) {
                  onChange({
                    dependsOnStableKey: rule.dependsOnStableKey,
                    operator: isNumericVisibilityOperator(rule.operator)
                      ? rule.operator
                      : "gte",
                    ...matrixCellFields({
                      threshold: nextThreshold,
                      thresholdHigh: highAfter(nextThreshold),
                    }),
                  });
                  return;
                }
                onChange(
                  plainNumericFields({
                    threshold: nextThreshold,
                    thresholdHigh: highAfter(nextThreshold),
                  }),
                );
              }}
              className={selectClass}
              data-testid={`questionnaire-visibility-rule-threshold-${testSuffix}`}
            >
              <option value="" disabled>
                …
              </option>
              {thresholdOptions.map((point) => (
                <option key={point} value={point}>
                  {point}
                </option>
              ))}
            </select>
            {isBetweenOperator ? (
              <>
                <span>and</span>
                <select
                  value={
                    typeof betweenThresholdHigh === "number"
                      ? String(betweenThresholdHigh)
                      : ""
                  }
                  disabled={disabled}
                  onChange={(event: ChangeEvent<HTMLSelectElement>) => {
                    const nextHigh = Number(event.target.value);
                    if (isMatrixDependency) {
                      onChange({
                        dependsOnStableKey: rule.dependsOnStableKey,
                        operator: "between",
                        ...matrixCellFields({
                          operator: "between",
                          thresholdHigh: nextHigh,
                        }),
                      });
                      return;
                    }
                    onChange(
                      plainNumericFields({
                        operator: "between",
                        thresholdHigh: nextHigh,
                      }),
                    );
                  }}
                  className={selectClass}
                  data-testid={`questionnaire-visibility-rule-threshold-high-${testSuffix}`}
                >
                  <option value="" disabled>
                    …
                  </option>
                  {thresholdOptions.map((point) => (
                    <option key={point} value={point}>
                      {point}
                    </option>
                  ))}
                </select>
              </>
            ) : null}
          </>
        ) : null}
        {isContinuousDependency ? (
          <>
            <input
              type="number"
              value={ruleThreshold ?? ""}
              min={selectedCandidate.min ?? selectedCandidate.rateMin}
              max={selectedCandidate.max ?? selectedCandidate.rateMax}
              disabled={disabled}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                const next = Number(event.target.value);
                if (!Number.isFinite(next)) {
                  return;
                }
                onChange(
                  plainNumericFields({
                    threshold: next,
                    thresholdHigh: highAfter(next),
                  }),
                );
              }}
              className={cn(selectClass, "w-24")}
              data-testid={`questionnaire-visibility-rule-threshold-${testSuffix}`}
            />
            {isBetweenOperator ? (
              <>
                <span>and</span>
                <input
                  type="number"
                  value={betweenThresholdHigh ?? ""}
                  min={selectedCandidate.min ?? selectedCandidate.rateMin}
                  max={selectedCandidate.max ?? selectedCandidate.rateMax}
                  disabled={disabled}
                  onChange={(event: ChangeEvent<HTMLInputElement>) => {
                    const next = Number(event.target.value);
                    if (!Number.isFinite(next)) {
                      return;
                    }
                    onChange(
                      plainNumericFields({
                        operator: "between",
                        thresholdHigh: next,
                      }),
                    );
                  }}
                  className={cn(selectClass, "w-24")}
                  data-testid={`questionnaire-visibility-rule-threshold-high-${testSuffix}`}
                />
              </>
            ) : null}
          </>
        ) : null}
        {canRemove ? (
          <button
            type="button"
            className="ml-auto flex h-6 w-6 shrink-0 items-center justify-center rounded-[4px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            disabled={disabled}
            onClick={() => onRemove()}
            aria-label={i18n.t("questionnaire.removeCondition", { ns: "app" })}
            data-testid={`questionnaire-visibility-rule-remove-${testSuffix}`}
          >
            <X className="h-3 w-3" />
          </button>
        ) : null}
      </div>
      {!isNumericDependency ? (
        <div className="flex flex-wrap gap-1.5">
          {selectedCandidate.options.map((option) => {
            const isActive = ruleValues.includes(option.value);
            const isLastActiveValue = isActive && ruleValues.length <= 1;
            return (
              <button
                key={option.id}
                type="button"
                className={cn(
                  "rounded-full border px-2.5 py-1 text-[11.5px] font-medium transition-colors",
                  isActive
                    ? "border-accent-highlight bg-accent-highlight/15 text-foreground"
                    : "border-border bg-background text-muted-foreground hover:border-accent-highlight/50 dark:bg-card",
                )}
                disabled={disabled}
                onClick={() => {
                  if (isRankingDependency) {
                    if (!multiValue) {
                      onChange(
                        rankingFields({ values: [option.value] }),
                      );
                      return;
                    }
                    if (isLastActiveValue) {
                      return;
                    }
                    onChange(
                      rankingFields({
                        values: isActive
                          ? ruleValues.filter(
                              (value) => value !== option.value,
                            )
                          : [...ruleValues, option.value],
                      }),
                    );
                    return;
                  }
                  if (!multiValue) {
                    onChange(choiceFields({ values: [option.value] }));
                    return;
                  }
                  if (isLastActiveValue) {
                    // A visibilityRule needs >=1 value — never let the last
                    // selected value be deselected via the chip.
                    return;
                  }
                  onChange(
                    choiceFields({
                      values: isActive
                        ? ruleValues.filter((value) => value !== option.value)
                        : [...ruleValues, option.value],
                    }),
                  );
                }}
                data-testid={`questionnaire-visibility-rule-value-${testSuffix}-${option.id}`}
              >
                {option.label || "Untitled option"}
              </button>
            );
          })}
        </div>
      ) : null}
    </div>
  );

}

export function QuestionnaireQuestionCard(
  props: QuestionnaireQuestionCardProps,
) {
  const { t } = useTranslation("app");
  const {
    conceptAssets,
    mentionTables,
    coverageResult,
    askedOffScreen,
    exposureZone,
    validationIssues,
    advisoryIssues,
    dependencyCandidates,
    routingWhenCandidates,
    routingTargetCandidates,
    choicesFromCandidates,
    feedsComposites,
    index,
    isSelected,
    language,
    contentLanguage,
    onChangeType,
    onDelete,
    onDuplicate,
    onSelect,
    onUpdate,
    question,
    disabled,
    disabledReason,
    questionLabelByStableKey,
    feedsQuestions,
    exposureSettings,
  } = props;

  // Which collapsible logic rows are open (D2). Saved rules start collapsed; a
  // rule added from the Settings menu opens expanded. Never persisted.
  const [expandedLogic, setExpandedLogic] = useState<
    Record<QuestionLogicKind, boolean>
  >({ visibility: false, choicesFrom: false, routing: false });

  const editableQuestion = question.question;
  const response = editableQuestion.response as any;
  const choicesFrom = (response as { choicesFrom?: ChoicesFromRef })
    .choicesFrom;
  const supportsChoicesFrom =
    response.kind === "single_select" ||
    response.kind === "multi_select" ||
    response.kind === "ranking";
  const choiceOptions = choiceOptionsOrDefaults(editableQuestion);
  const displayLabel = questionRefLabel(question.index);
  // The price version the question is about (D9): its title and description
  // chips name it "name · price", as Preview does.
  const ownVersionId = ownPriceVersion(editableQuestion.assetRefs, conceptAssets);
  // Exposure affordance (Phase 4b-f): ONE chip, whose state the panel resolved
  // from this question's position against the two declared marks. No detection
  // and no asset family here, which is what the three chips this replaced got
  // wrong on every family but ads.
  // Lift chips: tags live on the editable question (CanonicalEditableQuestion).
  const liftHalf = readQuestionLiftHalf(editableQuestion.tags);
  const isSelectResponse =
    response.kind === "single_select" || response.kind === "multi_select";
  const isGridResponse =
    response.kind === "matrix_rating" || response.kind === "matrix_categorical";
  const randomizeIsOn = isGridResponse
    ? gridRowsRandomized(response)
    : randomizeOptionsIsOn(response);
  // Plots plan C3: an ordered answer list is never shuffled, so shuffling is
  // not offered on one. The pill stays while shuffling is on, as its off switch.
  const isOrderedChoice = isSelectResponse && (response as any).ordered === true;

  const {
    attributes,
    isDragging,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: question.id, disabled: Boolean(disabled) });

  // "Add option" mounts a blank row. Focus it once that row is in the
  // document, in this commit's layout, before a chat effect can move the caret.
  const pendingChoiceFocusTestIdRef = useRef<string | null>(null);
  useLayoutEffect(() => {
    const testId = pendingChoiceFocusTestIdRef.current;
    if (!testId) {
      return;
    }
    pendingChoiceFocusTestIdRef.current = null;
    focusFieldByTestId(testId);
  });

  const sortableStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const visibilityCandidates = dependencyCandidates ?? [];
  const whenCandidates = routingWhenCandidates ?? [];
  const targetCandidates = routingTargetCandidates ?? [];
  const carryForwardCandidates = choicesFromCandidates ?? [];
  const routingInitialCandidate = initialRoutingCandidate(
    whenCandidates,
    editableQuestion.stableKey,
  );
  const logicSummaryContext = useMemo(() => {
    // Option labels for the summaries: earlier questions plus the host.
    const byStableKey = new Map<string, VisibilityRuleCandidate>();
    [...(dependencyCandidates ?? []), ...(routingWhenCandidates ?? [])].forEach(
      (candidate) => {
        if (!byStableKey.has(candidate.stableKey)) {
          byStableKey.set(candidate.stableKey, candidate);
        }
      },
    );
    return {
      hostStableKey: editableQuestion.stableKey,
      candidates: Array.from(byStableKey.values()),
      questionLabelByStableKey: questionLabelByStableKey ?? new Map(),
    };
  }, [
    dependencyCandidates,
    editableQuestion.stableKey,
    questionLabelByStableKey,
    routingWhenCandidates,
  ]);
  // Memoized so `body` (below) does not see a new object identity every
  // render: keyed on everything routingOutcomeByOptionValue reads, including
  // the ctx inputs it forwards via logicSummaryContext (itself memoized).
  const routingOutcomes = useMemo(
    () =>
      isSelectResponse
        ? routingOutcomeByOptionValue(
            editableQuestion.routingRule,
            {
              stableKey: editableQuestion.stableKey,
              isMultiSelect: response.kind === "multi_select",
              options: choiceOptions,
            },
            logicSummaryContext,
          )
        : undefined,
    [
      isSelectResponse,
      editableQuestion.routingRule,
      editableQuestion.stableKey,
      response.kind,
      choiceOptions,
      logicSummaryContext,
    ],
  );
  const isScreener =
    editableQuestion.routingRule?.disposition === "screened_out";

  const expandLogic = (kind: QuestionLogicKind) =>
    setExpandedLogic((current) => ({ ...current, [kind]: true }));
  const toggleLogic = (kind: QuestionLogicKind) =>
    setExpandedLogic((current) => ({ ...current, [kind]: !current[kind] }));
  const onVisibilityRuleChange = (
    rule: QuestionVisibilityRuleExpr | undefined,
  ) =>
    onUpdate((currentQuestion) =>
      canonicalEditors.withVisibilityRule(currentQuestion, rule),
    );
  const onRoutingRuleChange = (rule: QuestionRoutingRule | undefined) =>
    onUpdate((currentQuestion) =>
      canonicalEditors.withRoutingRule(currentQuestion, rule),
    );
  const onChoicesFromChange = (next: ChoicesFromRef | undefined) =>
    onUpdate((currentQuestion) =>
      withSyncedChoicesFrom(currentQuestion, next, carryForwardCandidates),
    );
  const onToggleRandomize = () =>
    onUpdate((currentQuestion) =>
      canonicalEditors.responseWithRandomizeOptions(
        currentQuestion,
        !randomizeIsOn,
      ),
    );

  // Menu items add a rule with the same default the removed footer "add"
  // links built, then open its row; an existing rule just opens.
  const logicItems: Array<QuestionSettingsLogicItem> = [
    {
      kind: "visibility",
      isListed: true,
      isActive: Boolean(editableQuestion.visibilityRule),
      isAvailable:
        Boolean(editableQuestion.visibilityRule) ||
        visibilityCandidates.length > 0,
      unavailableReason:
        index > 0
          ? t("questionnaire.logic.unavailableVisibility")
          : t("questionnaire.logic.unavailableFirstQuestion"),
      onSelect: () => {
        const source = nearestCandidate(visibilityCandidates);
        if (!editableQuestion.visibilityRule && source) {
          onVisibilityRuleChange(defaultRuleForCandidate(source));
        }
        expandLogic("visibility");
      },
    },
    {
      kind: "choicesFrom",
      isListed: supportsChoicesFrom,
      isActive: Boolean(choicesFrom),
      isAvailable:
        Boolean(choicesFrom) || carryForwardCandidates.length > 0,
      unavailableReason: t("questionnaire.logic.unavailableChoicesFrom"),
      onSelect: () => {
        if (!choicesFrom && carryForwardCandidates.length > 0) {
          onChoicesFromChange(
            defaultChoicesFrom(carryForwardCandidates[0], editableQuestion),
          );
        }
        expandLogic("choicesFrom");
      },
    },
    {
      kind: "routing",
      isListed: true,
      isActive: Boolean(editableQuestion.routingRule),
      isAvailable:
        Boolean(editableQuestion.routingRule) ||
        Boolean(routingInitialCandidate),
      unavailableReason: t("questionnaire.logic.unavailableRouting"),
      onSelect: () => {
        if (!editableQuestion.routingRule && routingInitialCandidate) {
          onRoutingRuleChange(
            defaultRoutingRule(
              routingInitialCandidate,
              targetCandidates,
              editableQuestion.stableKey,
              defaultEndSurveyDisposition(editableQuestion),
            ),
          );
        }
        expandLogic("routing");
      },
    },
  ];

  const body = useMemo(() => {
    const addChoiceAndFocus = () => {
      const addedIndex = addedChoiceIndex(editableQuestion);
      pendingChoiceFocusTestIdRef.current = `questionnaire-question-choice-${question.id}-${addedIndex}`;
      onUpdate(withAddedChoice);
    };
    // A comparison keeps at least two of its tested assets; the asset panel
    // owns which ones the study tests.
    const optionRemovalRefusal = (choiceIndex: number) =>
      removingOptionLeavesTooFewParticipants(editableQuestion, choiceIndex)
        ? ruleCopy("asset_comparison_participants_too_few")
        : undefined;
    // An asset option of a comparison stands for its asset, so it cannot be
    // the opt-out (`asset_comparison_exclusive_invalid`).
    const exclusiveOffered = (choiceIndex: number) =>
      !(
        answersWithComparisonParticipants(editableQuestion) &&
        isAssetBackedChoice(choiceOptions[choiceIndex])
      );
    // "Catch-all" (`keepLast`, D12: one meaning, a catch-all that stays last)
    // is offered while the list is shuffled. An exclusive option is already an
    // escape that stays last, and an answer that stands for a tested asset or
    // a roster entry is a real answer, shuffled with the others. An option that
    // has the flag always shows it, whoever set it (the chat, AUTHOR, an
    // exclusive or asset answer the applier accepted it on), so it can be
    // taken off (F26 #13).
    const keepLastOffered = (choiceIndex: number) => {
      const option = choiceOptions[choiceIndex];
      if (option?.keepLast === true) return true;
      return (
        randomizeOptionsIsOn(response) &&
        option?.exclusive !== true &&
        !isAssetBackedChoice(option) &&
        !canonicalEditors.isRosterAnswer(editableQuestion.response, option)
      );
    };
    // A typed roster's asset answer is renamed through its asset, so every
    // question that lists it follows; the question's own opt-outs stay editable.
    const optionLabelRefusal = (choiceIndex: number) =>
      canonicalEditors.isRosterAnswer(
        editableQuestion.response,
        choiceOptions[choiceIndex],
      )
        ? i18n.t("questionnaire.rosterAnswerLabelTitle", { ns: "app" })
        : undefined;
    const sortableOptionCount = authorOrderedOptionCount(editableQuestion);
    const moveOption = (from: number, to: number) =>
      onUpdate((currentQuestion) => withChoiceMoved(currentQuestion, from, to));
    if (response.kind === "single_select" || response.kind === "multi_select") {
      return (
        <ChoiceEditor
          conceptAssets={conceptAssets}
          mentionTables={mentionTables}
          items={choiceOptions}
          mode={response.kind === "multi_select" ? "checkbox" : "radio"}
          readOnly={Boolean(choicesFrom) || Boolean(disabled)}
          onAdd={canAddChoice(editableQuestion) ? addChoiceAndFocus : undefined}
          onChange={(choiceIndex, value) =>
            onUpdate((currentQuestion) =>
              withChoiceLabelAt(currentQuestion, choiceIndex, value),
            )
          }
          onRemove={(choiceIndex) =>
            onUpdate((currentQuestion) =>
              withoutChoiceAt(currentQuestion, choiceIndex),
            )
          }
          sortableCount={sortableOptionCount}
          onMove={moveOption}
          removalRefusal={optionRemovalRefusal}
          labelRefusal={optionLabelRefusal}
          exclusiveOffered={exclusiveOffered}
          onToggleExclusive={
            response.kind === "multi_select"
              ? (choiceIndex, exclusive) =>
                  onUpdate((currentQuestion) =>
                    canonicalEditors.responseWithChoiceExclusive(
                      currentQuestion,
                      choiceIndex,
                      exclusive,
                    ),
                  )
              : undefined
          }
          keepLastOffered={keepLastOffered}
          onToggleKeepLast={(choiceIndex, keepLast) =>
            onUpdate((currentQuestion) =>
              canonicalEditors.responseWithChoiceKeepLast(
                currentQuestion,
                choiceIndex,
                keepLast,
              ),
            )
          }
          testIdPrefix={`questionnaire-question-choice-${question.id}`}
          contentLanguage={contentLanguage}
          outcomeByValue={routingOutcomes}
        />
      );
    }

    if (response.kind === "ranking") {
      return (
        <RankingEditor
          conceptAssets={conceptAssets}
          mentionTables={mentionTables}
          items={choiceOptions}
          minRankedItems={response.minRankedItems}
          maxRankedItems={response.maxRankedItems}
          readOnlyOptions={Boolean(choicesFrom) || Boolean(disabled)}
          disabled={disabled}
          onAdd={
            answersWithComparisonParticipants(editableQuestion)
              ? undefined
              : addChoiceAndFocus
          }
          onChange={(choiceIndex, value) =>
            onUpdate((currentQuestion) =>
              withChoiceLabelAt(currentQuestion, choiceIndex, value),
            )
          }
          onRemove={(choiceIndex) =>
            onUpdate((currentQuestion) =>
              withoutChoiceAt(currentQuestion, choiceIndex),
            )
          }
          sortableCount={sortableOptionCount}
          onMove={moveOption}
          removalRefusal={optionRemovalRefusal}
          labelRefusal={optionLabelRefusal}
          onMinChange={(value) =>
            onUpdate((currentQuestion) => {
              const next = {
                ...currentQuestion,
                response: { ...currentQuestion.response, minRankedItems: value } as typeof currentQuestion.response,
              };
              const carried = (next.response as { choicesFrom?: ChoicesFromRef })
                .choicesFrom;
              return carried
                ? withSyncedChoicesFrom(next, carried, carryForwardCandidates)
                : next;
            })
          }
          onMaxChange={(value) =>
            onUpdate((currentQuestion) => ({
              ...currentQuestion,
              response: { ...currentQuestion.response, maxRankedItems: value } as typeof currentQuestion.response,
            }))
          }
          testIdPrefix={`questionnaire-question-choice-${question.id}`}
          contentLanguage={contentLanguage}
        />
      );
    }

    if (response.kind === "rating") {
      return (
        <RatingEditor
          question={editableQuestion}
          onUpdate={onUpdate}
          questionId={question.id}
          language={language}
          contentLanguage={contentLanguage}
          disabled={disabled}
        />
      );
    }

    if (response.kind === "continuous") {
      return (
        <ContinuousEditor
          question={editableQuestion}
          onUpdate={onUpdate}
          questionId={question.id}
          contentLanguage={contentLanguage}
          disabled={disabled}
        />
      );
    }

    if (response.kind === "matrix_rating") {
      return (
        <MatrixRatingEditor
          conceptAssets={conceptAssets}
          mentionTables={mentionTables}
          question={editableQuestion}
          onUpdate={onUpdate}
          questionId={question.id}
          language={language}
          contentLanguage={contentLanguage}
          disabled={disabled}
          disabledReason={disabledReason}
        />
      );
    }

    if (response.kind === "matrix_categorical") {
      return (
        <MatrixCategoricalEditor
          conceptAssets={conceptAssets}
          mentionTables={mentionTables}
          question={editableQuestion}
          onUpdate={onUpdate}
          questionId={question.id}
          contentLanguage={contentLanguage}
          disabled={disabled}
          disabledReason={disabledReason}
        />
      );
    }

    if (response.kind === "open_end") {
      return (
        <Textarea
          value={
            typeof response.placeholder === "string" ? response.placeholder : ""
          }
          onChange={(event: ChangeEvent<HTMLTextAreaElement>) =>
            onUpdate((currentQuestion) => ({
              ...currentQuestion,
              response: {
                ...(currentQuestion.response as any),
                placeholder: event.target.value,
              },
            }))
          }
          rows={2}
          disabled={disabled}
          className="min-h-[44px] resize-none rounded-[8px] border-border bg-transparent p-3 text-[13px] text-foreground"
          data-testid={`questionnaire-question-placeholder-${question.id}`}
        />
      );
    }

    return null;
  }, [
    choiceOptions,
    choicesFrom,
    conceptAssets,
    mentionTables,
    contentLanguage,
    disabled,
    disabledReason,
    editableQuestion,
    language,
    onUpdate,
    question.id,
    response,
    routingOutcomes,
  ]);

  return (
    <div
      ref={setNodeRef}
      style={sortableStyle}
      className={cn(
        "group relative rounded-[12px] border bg-background px-[26px] py-[22px] transition-[border-color,box-shadow,transform] duration-150 dark:bg-card",
        isSelected
          ? "border-accent-highlight shadow-[0_0_0_3px_hsl(var(--accent-highlight)/0.15)]"
          : "border-border hover:border-border/80 hover:shadow-[0_2px_14px_rgba(7,18,15,0.06)]",
        isDragging &&
          "pointer-events-none opacity-25 [&_*]:!cursor-grabbing",
      )}
      data-testid={`questionnaire-question-card-${question.id}`}
      data-exposure-zone={exposureZone}
      onClick={onSelect}
    >
      <fieldset disabled={disabled} className="m-0 min-w-0 border-0 p-0">
      <TooltipProvider delayDuration={600}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              {...attributes}
              {...listeners}
              onClick={(event) => event.stopPropagation()}
              aria-label={t("questionnaire.reorderQuestion")}
              disabled={disabled}
              title={disabled ? disabledReason : undefined}
              className="absolute left-1 top-1/2 hidden -translate-y-1/2 touch-none cursor-grab rounded-md p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-muted/60 hover:text-foreground group-hover:opacity-70 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-highlight active:cursor-grabbing md:block"
              data-testid={`questionnaire-question-drag-${question.id}`}
            >
              <GripVertical className="h-3.5 w-3.5" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="left">
            <span className="text-[11px]">{t("questionnaire.dragToReorder")}</span>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <div className="min-w-0 flex-1">
        {/* Header row: Q badge + type label + action buttons */}
        <div className="mb-2 flex items-center gap-2">
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#DCF0EB] text-[11px] font-normal leading-none text-[#146558] dark:bg-primary/10 dark:text-primary">
            {displayLabel}
          </div>
          <TypePicker
            question={editableQuestion}
            onChange={onChangeType}
            questionId={question.id}
            disabled={disabled}
            disabledReason={disabledReason}
          />
          {feedsComposites && feedsComposites.length > 0 ? (
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className={cn(
                      PILL_BASE_CLASS,
                      "cursor-default border-transparent bg-muted text-foreground",
                    )}
                    data-testid={`questionnaire-feeds-score-${question.id}`}
                  >
                    {t("questionnaire.logic.pillScore", {
                      labels: feedsComposites
                        .map(compositeScoreLabel)
                        .join(", "),
                    })}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[240px]">
                  <span className="text-[11px]">
                    {t("questionnaire.logic.pillScoreTooltip", {
                      labels: feedsComposites
                        .map(compositeScoreLabel)
                        .join(", "),
                    })}
                  </span>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : null}
          {askedOffScreen ? (
            <span
              className="inline-flex items-center gap-1 text-[11px] leading-[1.4] text-muted-foreground"
              data-testid={`questionnaire-offscreen-${question.id}`}
            >
              <EyeOff className="h-3 w-3" aria-hidden />
              {t("questionnaire.exposureOffScreen")}
            </span>
          ) : null}
          {editableQuestion.visibilityRule ? (
            <button
              type="button"
              className={cn(
                PILL_BASE_CLASS,
                "border-border bg-muted text-muted-foreground hover:text-foreground",
              )}
              onClick={() => expandLogic("visibility")}
              data-testid={`questionnaire-conditional-pill-${question.id}`}
              title={t("questionnaire.skipDepends")}
            >
              <GitBranch className="h-3 w-3" />
              {t("questionnaire.logic.pillAskedIf")}
            </button>
          ) : null}
          {editableQuestion.routingRule ? (
            <button
              type="button"
              className={cn(
                PILL_BASE_CLASS,
                isScreener
                  ? "border-destructive/25 bg-destructive/10 text-destructive"
                  : "border-border bg-muted text-muted-foreground hover:text-foreground",
              )}
              onClick={() => expandLogic("routing")}
              data-testid={`questionnaire-routing-pill-${question.id}`}
              title={
                isScreener
                  ? t("questionnaire.logic.pillScreenerTooltip")
                  : editableQuestion.routingRule.action === "end_survey"
                    ? t("questionnaire.logic.pillEndsSurveyTooltip")
                    : t("questionnaire.logic.pillSkipsAheadTooltip")
              }
            >
              <GitBranch className="h-3 w-3" />
              {isScreener
                ? t("questionnaire.logic.pillScreener")
                : editableQuestion.routingRule.action === "end_survey"
                  ? t("questionnaire.logic.pillEndsSurvey")
                  : t("questionnaire.logic.pillSkipsAhead")}
            </button>
          ) : null}
          {choicesFrom ? (
            <button
              type="button"
              className={cn(
                PILL_BASE_CLASS,
                "border-border bg-muted text-muted-foreground hover:text-foreground",
              )}
              onClick={() => expandLogic("choicesFrom")}
              data-testid={`questionnaire-choices-from-pill-${question.id}`}
              title={t("questionnaire.optionsCarried")}
            >
              <Filter className="h-3 w-3" />
              {t("questionnaire.logic.pillReusedOptions")}
            </button>
          ) : null}
          {feedsQuestions && feedsQuestions.length > 0 ? (
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className={cn(
                      PILL_BASE_CLASS,
                      "cursor-default border-border bg-muted text-muted-foreground",
                    )}
                    data-testid={`questionnaire-feeds-questions-${question.id}`}
                  >
                    <Filter className="h-3 w-3" />
                    {t("questionnaire.logic.pillFeeds", {
                      labels: feedsQuestions.join(", "),
                    })}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[240px]">
                  <span className="text-[11px]">
                    {t("questionnaire.logic.pillFeedsTooltip", {
                      labels: feedsQuestions.join(", "),
                      count: feedsQuestions.length,
                    })}
                  </span>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : null}
          {liftHalf ? (
            <LiftHalfChip
              half={liftHalf.half}
              testId={
                liftHalf.half === "pre"
                  ? `questionnaire-lift-pre-${question.id}`
                  : `questionnaire-lift-post-${question.id}`
              }
            />
          ) : null}
          <TopicCoverageChip
            coverageResult={coverageResult}
            questionId={question.id}
          />
          <ValidationIssueChip
            questionId={question.id}
            validationIssues={validationIssues}
          />
          {(isSelectResponse || isGridResponse) && randomizeIsOn ? (
            <RandomizePill
              isOn={randomizeIsOn}
              rows={isGridResponse}
              onToggle={onToggleRandomize}
              disabled={disabled}
              disabledReason={disabledReason}
              questionId={question.id}
            />
          ) : null}
          <QuestionSettingsMenu
            questionId={question.id}
            logicItems={logicItems}
            randomize={
              isGridResponse ||
              (isSelectResponse && (!isOrderedChoice || randomizeIsOn))
                ? { isOn: randomizeIsOn, onToggle: onToggleRandomize }
                : undefined
            }
            exposure={exposureSettings}
            disabled={disabled}
            disabledReason={disabledReason}
          />
          <span className="flex-1" />
          <TooltipProvider delayDuration={400}>
            <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 rounded-[6px]"
                    onClick={onDuplicate}
                    disabled={disabled}
                    title={disabled ? disabledReason : undefined}
                    aria-label={t("questionnaire.duplicateQuestion")}
                    data-testid={`questionnaire-duplicate-question-${question.id}`}
                  >
                    <Copy className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <span className="text-[11px]">{t("questionnaire.duplicateQuestion")}</span>
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 rounded-[6px]"
                    onClick={onDelete}
                    disabled={disabled}
                    title={disabled ? disabledReason : undefined}
                    aria-label={t("questionnaire.deleteQuestion")}
                    data-testid={`questionnaire-delete-question-${question.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  <span className="text-[11px]">{t("questionnaire.deleteQuestion")}</span>
                </TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>
        </div>

        {editableQuestion.placeholderSubstitutions &&
        editableQuestion.placeholderSubstitutions.length > 0 ? (
          <p
            className="mb-2 rounded-[8px] border border-chart-warning/30 bg-chart-warning/10 px-2.5 py-1.5 text-[12px] leading-[1.45] text-foreground"
            data-testid={`questionnaire-placeholder-substitution-notice-${question.id}`}
          >
            We could not read the answer options for this question. Please
            replace them before launching.
          </p>
        ) : null}

        {advisoryIssues?.map((issue) => (
          <p
            key={`${issue.code}:${issue.path}`}
            className="mb-2 rounded-[8px] border border-chart-warning/30 bg-chart-warning/10 px-2.5 py-1.5 text-[12px] leading-[1.45] text-foreground"
            data-testid={`questionnaire-advisory-${question.id}`}
          >
            {formatIssueCopy(issue).text}
          </p>
        ))}

        {/* Title */}
        <ConceptMentionField
          value={editableQuestion.title || ""}
          onChange={(value) =>
            onUpdate((currentQuestion) => ({
              ...currentQuestion,
              title: value,
            }))
          }
          conceptAssets={conceptAssets}
          mentionTables={mentionTables}
          contentLanguage={contentLanguage}
          boundAssetId={ownVersionId}
          rows={2}
          disabled={disabled}
          className="mb-1 min-h-[26px] w-full resize-none rounded-[4px] border-0 bg-transparent px-1 py-0 text-[16px] font-medium leading-[1.45] tracking-normal text-foreground shadow-none transition-colors hover:bg-muted/50 focus-visible:bg-primary/10 focus-visible:ring-0 dark:focus-visible:bg-accent"
          placeholder={t("questionnaire.writePrompt")}
          data-testid={`questionnaire-question-title-${question.id}`}
        />

        {"description" in editableQuestion ? (
          <ConceptMentionField
            value={
              typeof editableQuestion.description === "string"
                ? editableQuestion.description
                : ""
            }
            onChange={(value) =>
              onUpdate((currentQuestion) => ({
                ...currentQuestion,
                description: value,
              }))
            }
            conceptAssets={conceptAssets}
            mentionTables={mentionTables}
            contentLanguage={contentLanguage}
            boundAssetId={ownVersionId}
            rows={1}
            disabled={disabled}
            className="mb-2 min-h-[20px] resize-none rounded-[4px] border-0 bg-transparent px-1 py-0 text-[12px] leading-[1.45] text-muted-foreground shadow-none transition-colors hover:bg-muted/50 focus-visible:bg-primary/10 focus-visible:ring-0 dark:focus-visible:bg-accent"
            placeholder={t("questionnaire.optionalContext")}
            data-testid={`questionnaire-question-description-${question.id}`}
          />
        ) : null}

        {/* Rule rows (D2): fixed order, one collapsible row per existing rule. */}
        {editableQuestion.visibilityRule ? (
          <QuestionLogicSection
            icon={<GitBranch className="h-3 w-3" />}
            summary={summarizeVisibilityRule(
              editableQuestion.visibilityRule,
              logicSummaryContext,
            )}
            isExpanded={disabled || expandedLogic.visibility}
            onToggle={() => toggleLogic("visibility")}
            onRemove={() => onVisibilityRuleChange(undefined)}
            testIdPrefix="questionnaire-visibility-rule"
            questionId={question.id}
            disabled={disabled}
          >
            <VisibilityRuleEditor
              candidates={withQuestionLabels(
                visibilityCandidates,
                questionLabelByStableKey,
              )}
              onChange={onVisibilityRuleChange}
              questionId={question.id}
              rule={editableQuestion.visibilityRule}
              disabled={disabled}
            />
          </QuestionLogicSection>
        ) : null}

        {supportsChoicesFrom && choicesFrom ? (
          <QuestionLogicSection
            icon={<Filter className="h-3 w-3" />}
            summary={summarizeChoicesFrom(choicesFrom, logicSummaryContext)}
            isExpanded={disabled || expandedLogic.choicesFrom}
            onToggle={() => toggleLogic("choicesFrom")}
            onRemove={() => onChoicesFromChange(undefined)}
            testIdPrefix="questionnaire-choices-from"
            questionId={question.id}
            disabled={disabled}
          >
            <ChoicesFromEditor
              candidates={withQuestionLabels(
                carryForwardCandidates,
                questionLabelByStableKey,
              )}
              choicesFrom={choicesFrom}
              target={editableQuestion}
              onChange={onChoicesFromChange}
              questionId={question.id}
              disabled={disabled}
            />
          </QuestionLogicSection>
        ) : null}

        {editableQuestion.routingRule ? (
          <QuestionLogicSection
            icon={<GitBranch className="h-3 w-3" />}
            summary={summarizeRoutingRule(
              editableQuestion.routingRule,
              logicSummaryContext,
            )}
            tone={isScreener ? "destructive" : "default"}
            isExpanded={disabled || expandedLogic.routing}
            onToggle={() => toggleLogic("routing")}
            onRemove={() => onRoutingRuleChange(undefined)}
            testIdPrefix="questionnaire-routing-rule"
            questionId={question.id}
            disabled={disabled}
          >
            <RoutingRuleEditor
              whenCandidates={withQuestionLabels(
                whenCandidates,
                questionLabelByStableKey,
              )}
              targetCandidates={withQuestionLabels(
                targetCandidates,
                questionLabelByStableKey,
              )}
              hostStableKey={editableQuestion.stableKey}
              endSurveyDisposition={defaultEndSurveyDisposition(editableQuestion)}
              onChange={onRoutingRuleChange}
              questionId={question.id}
              rule={editableQuestion.routingRule}
              disabled={disabled}
            />
          </QuestionLogicSection>
        ) : null}

        {body}

      </div>
      </fieldset>
    </div>
  );
}

export function DraggedQuestionPreview(props: {
  record: QuestionnaireQuestionRecord;
}) {
  const { record } = props;
  const { question } = record;
  const displayLabel = questionRefLabel(record.index);
  const tone = chipTone(question);
  const title =
    typeof question.title === "string" && question.title.trim().length > 0
      ? question.title
      : "Untitled question";

  return (
    <div
      className="pointer-events-none flex max-w-[640px] items-center gap-3 rounded-[12px] border border-accent-highlight bg-background px-4 py-2.5 shadow-[0_12px_28px_rgba(7,18,15,0.18)] dark:bg-card"
      data-testid="questionnaire-question-drag-preview"
    >
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[#DCF0EB] text-[11px] font-normal leading-none text-[#146558] dark:bg-primary/10 dark:text-primary">
        {displayLabel}
      </div>
      <span className="min-w-0 flex-1 truncate text-[14px] font-medium leading-[1.45] text-foreground">
        {title}
      </span>
      <span
        className={cn(
          "shrink-0 rounded-full border px-[10px] py-[3px] text-[11px] font-medium leading-[1.4]",
          tone === "coral"
            ? "border-chart-warning/30 bg-chart-warning/10 text-foreground"
            : tone === "green"
              ? "border-primary/25 bg-primary/10 text-foreground"
              : "border-border bg-muted text-muted-foreground",
        )}
      >
        {typeLabel(question)}
      </span>
    </div>
  );
}
